// src/modules/production/ProductionPhasePage.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import {
    addDoc, collection, getDocs, getDoc, updateDoc, doc, query, where,
    onSnapshot, serverTimestamp, runTransaction, Timestamp, writeBatch, arrayUnion,
    deleteDoc
} from 'firebase/firestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import { exportProductionDocument } from '../../documents/exportProductionDocument';
import styles from './Production.module.css';
import { exportProcessoManipulacao, exportProcessoEnvase, exportProcessoControleQualidade } from '../../documents';

// Reusable Components
import { Toast, EmptyState, ConfirmModal, PageHeader } from '../../components/common';

// Notification Context
import { useNotifications } from '../../contexts/NotificationContext';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiDownload,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiUpload,
    FiPrinter,
    FiChevronUp,
    FiChevronDown,
    FiClock,
    FiUser,
    FiBox,
    FiDroplet,
    FiFolder,
    FiHome,
    FiBriefcase,
    FiTruck,
    FiShoppingCart,
    FiDollarSign,
    FiPercent,
    FiCreditCard,
    FiSend,
    FiFlag,
    FiCheckCircle,
    FiXCircle,
    FiClock as FiClockIcon,
    FiTrendingUp,
    FiUsers,
    FiPhone,
    FiMail,
    FiMap,
    FiNavigation,
    FiCalendar as FiCalendarIcon,
    FiMessageSquare,
    FiSettings,
    FiStar,
    FiHeart,
    FiThumbsUp,
    FiThumbsDown,
    FiMoreHorizontal,
    FiAward,
    FiDollarSign as FiMoney,
    FiArrowLeft as FiArrowLeftIcon,
    FiArrowRight as FiArrowRightIcon,
    FiPlusCircle,
    FiMinusCircle,
    FiInbox,
    FiExternalLink,
    FiHelpCircle,
    FiAlertTriangle,
    FiCheckCircle as FiCheckCircleIcon,
    FiClock as FiClockIcon2,
    FiTag as FiTagIcon,
    FiFlag as FiFlagIcon,
    FiUsers as FiUsersIcon,
    FiUser as FiUserIcon,
    FiCalendar as FiCalendarIcon2,
    FiMessageSquare as FiMessageSquareIcon,
    FiSend as FiSendIcon,
    FiBarChart2,
    FiPieChart,
    FiFile,
    FiFolder as FiFolderIcon,
    FiTrash,
    FiEdit2,
    FiEye as FiEyeIcon,
    FiPlus as FiPlusIcon,
    FiMinus,
    FiXOctagon,
    FiCheckSquare,
    FiSquare,
    FiArrowUp,
    FiArrowDown,
    FiFilter,
    FiGrid,
    FiList as FiListIcon,
    FiMaximize2,
    FiMinimize2,
    FiPlay,
    FiPause,
    FiStopCircle,
    FiSkipForward,
    FiSkipBack
} from 'react-icons/fi';
import {
    MdEdit,
    MdDelete,
    MdAdd,
    MdSave,
    MdCancel,
    MdVisibility,
    MdPrint,
    MdLocationOn,
    MdBusiness,
    MdPerson,
    MdPhone,
    MdEmail,
    MdAttachMoney,
    MdReceipt,
    MdLocalShipping,
    MdCheckCircle,
    MdCancel as MdCancelIcon,
    MdPending,
    MdApproval,
    MdProductionQuantityLimits,
    MdDeliveryDining,
    MdFactCheck,
    MdMoney,
    MdMonetizationOn,
    MdOutlineCheckCircle,
    MdOutlineCancel,
    MdOutlinePending,
    MdInventory,
    MdHelp,
    MdSupport,
    MdAssignment,
    MdPeople,
    MdPersonOutline,
    MdCategory,
    MdPriorityHigh,
    MdAccessTime,
    MdAssignmentInd,
    MdQrCode,
    MdPrint as MdPrintIcon,
    MdShowChart,
    MdTimeline,
    MdProductionQuantityLimits as MdProductionIcon,
    MdFactCheck as MdFactCheckIcon,
    MdOutlineProductionQuantityLimits,
    MdPlayArrow,
    MdPause,
    MdStop,
    MdSkipNext,
    MdSkipPrevious
} from 'react-icons/md';
import { FaBox, FaTruck, FaUser, FaBuilding, FaMoneyBillWave, FaClipboardList, FaDollarSign, FaPrint, FaCheck, FaTimes, FaEye, FaMapMarkerAlt, FaBoxOpen, FaWarehouse, FaTicketAlt, FaClock, FaFlag, FaUserTag, FaUsers, FaTasks, FaBarcode, FaQrcode, FaChartLine, FaChartBar, FaFileExport, FaFileImport, FaPlusCircle, FaMinusCircle as FaMinusCircleIcon, FaTrashAlt, FaPlay, FaPause, FaStop, FaForward, FaBackward, FaChevronRight } from 'react-icons/fa';

// ============================================================================
// CONSTANTS
// ============================================================================
const PRODUCTION_ORDERS_COLLECTION = 'production-orders';
const PRODUCTS_COLLECTION = 'products';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const PROCEDURES_COLLECTION = 'procedures';
const STOCK_COLLECTIONS = ['current-stock'];
const STOCK_OUTFLOW_MAP = {
    'current-stock': 'stock-outflows',
};
const STOCK_ENTRY_MAP = {
    'current-stock': 'stock-entries',
};
const CATEGORY_COLLECTION_PATH = 'parameters/data/product-categories';
const WATER_MATERIAL_CODE = 'MP001';
const CURRENT_STOCK_COLLECTION = 'current-stock';
const STOCK_ENTRIES_COLLECTION = 'stock-entries';

// ============================================================================
// PHASE MAPPING - Product Selection to System Phases
// ============================================================================
const PRODUCT_TO_SYSTEM_PHASES = {
    'Pesagem': ['phase-1'],
    'Producao': ['phase-2', 'phase-3'],
    'ControleQualidadeProcesso': ['phase-4', 'phase-5', 'phase-6'],
    'ConferenciaEmbalagem': ['phase-7'],
    'Envase': ['phase-8', 'phase-9'],
    'Rotulagem': ['phase-10']
};

// Nomes amigáveis para exibição
const PHASE_DISPLAY_NAMES = {
    'phase-1': 'Pesagem',
    'phase-2': 'Início da Produção',
    'phase-3': 'Término da Produção',
    'phase-4': 'Início do Controle de Qualidade',
    'phase-5': 'Controle de Qualidade em Processo',
    'phase-6': 'Liberação do Controle de Qualidade',
    'phase-7': 'Conferência de Embalagem',
    'phase-8': 'Início do Envase',
    'phase-9': 'Término do Envase',
    'phase-10': 'Término da Rotulagem'
};

// Todas as fases do sistema (definição completa)
const ALL_SYSTEM_PHASES = [
    {
        id: 'phase-1',
        number: 1,
        name: 'Pesagem',
        description: 'Pesagem precisa de cada componente conforme formulacao',
        requiresWeighing: true,
    },
    {
        id: 'phase-2',
        number: 2,
        name: 'Início da Produção',
        description: 'Registro do inicio da producao com data de fabricacao e validade',
        requiresWeighing: false,
    },
    {
        id: 'phase-3',
        number: 3,
        name: 'Término da Produção',
        description: 'Registro do termino da producao',
        requiresWeighing: false,
    },
    {
        id: 'phase-4',
        number: 4,
        name: 'Início do Controle de Qualidade',
        description: 'Registro do inicio das analises de controle de qualidade durante o processo',
        requiresWeighing: false,
    },
    {
        id: 'phase-5',
        number: 5,
        name: 'Controle de Qualidade em Processo',
        description: 'Registro dos resultados das analises de controle de qualidade em processo',
        requiresWeighing: false,
    },
    {
        id: 'phase-6',
        number: 6,
        name: 'Liberação do Controle de Qualidade',
        description: 'Registro da liberacao do lote apos aprovacao do controle de qualidade',
        requiresWeighing: false,
    },
    {
        id: 'phase-7',
        number: 7,
        name: 'Conferência de Embalagem',
        description: 'Verificacao das embalagens a serem utilizadas no envase',
        requiresWeighing: false,
    },
    {
        id: 'phase-8',
        number: 8,
        name: 'Início do Envase',
        description: 'Registro do inicio da operacao de envase com configuracao de equipamentos',
        requiresWeighing: false,
    },
    {
        id: 'phase-9',
        number: 9,
        name: 'Término do Envase',
        description: 'Registro do termino da operacao de envase',
        requiresWeighing: false,
    },
    {
        id: 'phase-10',
        number: 10,
        name: 'Término da Rotulagem',
        description: 'Registro do termino da rotulagem com quantidades e conferencias',
        requiresWeighing: false,
    }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const isWaterMaterial = (material) => {
    if (!material) return false;
    return material.code === WATER_MATERIAL_CODE || material.id === WATER_MATERIAL_CODE;
};

const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        const expiry = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expiry < today;
    } catch {
        return false;
    }
};

const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
        const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
        return date.toLocaleDateString('pt-BR');
    } catch {
        return null;
    }
};

// ============================================================================
// ADD FINISHED PRODUCT TO STOCK
// ============================================================================

const addFinishedProductToStock = async (orderId, orderData) => {
    if (!orderData || !orderData.items || orderData.items.length === 0) {
        console.error('No product items found in order');
        return false;
    }

    const item = orderData.items[0];
    if (!item.productId) {
        console.error('No productId found in order items');
        return false;
    }

    // Get product details
    const productRef = doc(db, PRODUCTS_COLLECTION, item.productId);
    const productSnap = await getDoc(productRef);

    if (!productSnap.exists()) {
        console.error(`Product ${item.productId} not found`);
        return false;
    }

    const productData = productSnap.data();
    const productCode = productData.code || item.productCode;
    const productName = productData.commercialName || productData.name || item.productName;

    // Get gramatura (weight per unit)
    const technical = productData.technical || {};
    const gramatura = parseFloat(technical.gramatura) || parseFloat(productData.gramatura) || 0;
    const gramaturaUnit = technical.gramaturaUnit || productData.gramaturaUnit || 'g';

    // Convert to kg per unit (base unit for stock)
    const kgPerUnit = (gramaturaUnit === 'g' || gramaturaUnit === 'ml') ? gramatura / 1000 : gramatura;

    let unitCount = 0;
    let totalKg = 0;
    const mode = item.mode || 'mass';

    if (mode === 'units') {
        // User provided quantity in UNITS - store as units
        unitCount = parseFloat(item.quantity) || 0;
        totalKg = unitCount * kgPerUnit;
        console.log(`Units mode: ${unitCount} units × ${kgPerUnit}kg/unit = ${totalKg}kg`);
    } else {
        // Mode is 'mass' - quantity is in kg, convert to units
        const kgQuantity = parseFloat(item.quantity) || 0;
        if (kgPerUnit > 0) {
            unitCount = kgQuantity / kgPerUnit;
            totalKg = kgQuantity;
            console.log(`Mass mode: ${kgQuantity}kg / ${kgPerUnit}kg/unit = ${unitCount} units`);
        } else {
            console.error('Invalid gramatura (zero or missing) for mass conversion');
            return false;
        }
    }

    if (unitCount <= 0) {
        console.error('Invalid unit count:', unitCount);
        return false;
    }

    // Determine expiration date from fabrication date + shelf life
    const fabricationDate = orderData.fabricationDate || new Date().toISOString().split('T')[0];
    const shelfLifeMonths = technical.shelfLifeMonths || productData.shelfLifeMonths || 24;

    const fabDate = new Date(fabricationDate);
    const expDate = new Date(fabDate);
    expDate.setMonth(expDate.getMonth() + parseInt(shelfLifeMonths));
    const expirationDateStr = expDate.toISOString().split('T')[0];

    // Use OP code as lot number if no specific lot is defined
    const lotNumber = item.lote || orderData.opCode || `OP-${orderId}`;

    // Get product location from phase10 data if available
    let locationId = 'PRODUTO_ACABADO'; // Default location

    if (orderData.phase10Data && orderData.phase10Data.location) {
        locationId = orderData.phase10Data.location;
    } else if (orderData.finishedProductLocation) {
        locationId = orderData.finishedProductLocation;
    } else if (orderData.location) {
        locationId = orderData.location;
    }

    // Create stock entry for the finished product
    const stockId = `${item.productId}_${locationId}_${lotNumber}`;
    const currentStockRef = doc(db, 'current-stock', stockId);

    try {
        await runTransaction(db, async (transaction) => {
            const stockDoc = await transaction.get(currentStockRef);
            const currentQuantity = stockDoc.exists() ? (stockDoc.data().quantity || 0) : 0;
            const newQuantity = currentQuantity + unitCount;

            const stockData = {
                rawMaterialId: item.productId,
                locationId: locationId,
                quantity: newQuantity,
                rawMaterialCode: productCode,
                rawMaterialName: productName,
                locationDescription: locationId === 'PRODUTO_ACABADO' ? 'Produto Acabado' : locationId,
                updatedAt: serverTimestamp(),
                createdAt: stockDoc.exists() ? stockDoc.data().createdAt : serverTimestamp(),
                lotNumber: lotNumber,
                expirationDate: expirationDateStr,
                manufacturerDate: fabricationDate,
                isProduct: true,
                sourceOrderId: orderId,
                sourceOrderCode: orderData.opCode,
                // Store conversion info for reference
                originalMode: mode,
                originalQuantity: item.quantity,
                gramatura: gramatura,
                gramaturaUnit: gramaturaUnit,
                kgPerUnit: kgPerUnit,
                unitCount: unitCount,
                totalKg: totalKg
            };

            transaction.set(currentStockRef, stockData);

            // Also record the entry in stock-entries for traceability
            const entryRef = doc(collection(db, 'stock-entries'));
            transaction.set(entryRef, {
                rawMaterialId: item.productId,
                rawMaterialCode: productCode,
                rawMaterialName: productName,
                locationIds: [locationId],
                locationQuantities: { [locationId]: unitCount },
                quantity: unitCount,
                unit: 'Un',
                isProduct: true,
                type: 'ENTRADA',
                notes: `Produção finalizada - OP: ${orderData.opCode} | ${unitCount} unidades${mode === 'mass' ? ` (${item.quantity}kg → ${unitCount} un @ ${kgPerUnit}kg/un)` : ''}`,
                createdAt: serverTimestamp(),
                createdBy: 'Sistema (Produção)',
                lotNumber: lotNumber,
                expirationDate: expirationDateStr,
                manufacturerDate: fabricationDate,
                sourceOrderId: orderId,
                sourceOrderCode: orderData.opCode,
                originalMode: mode,
                originalQuantity: item.quantity,
                gramatura: gramatura,
                gramaturaUnit: gramaturaUnit,
                kgPerUnit: kgPerUnit,
                unitCount: unitCount,
                totalKg: totalKg
            });
        });

        const unitDisplay = unitCount % 1 === 0 ? unitCount : unitCount.toFixed(3);
        console.log(`✅ Product ${productCode} added to stock: ${unitDisplay} units${mode === 'mass' ? ` (${item.quantity}kg → ${kgPerUnit}kg/un)` : ''} at ${locationId}`);
        return true;
    } catch (error) {
        console.error('Error adding finished product to stock:', error);
        throw error;
    }
};

// ============================================================================
// ATOMIC STOCK DEDUCTION WITH BATCH SUPPORT
// ============================================================================
const executeAtomicStockDeductionWithBatches = async (batchSelectionsMap, orderId, opCode) => {
    if (!batchSelectionsMap || typeof batchSelectionsMap !== 'object') {
        console.error('batchSelectionsMap inválido:', batchSelectionsMap);
        return [];
    }

    const allSelections = [];
    Object.entries(batchSelectionsMap).forEach(([key, selection]) => {
        if (!selection) return;
        const qty = parseFloat(selection.selectedQuantity || 0);
        if (qty <= 0.0001) return;
        allSelections.push({
            batchId: selection.batchId,
            selectedQuantity: qty,
            materialName: selection.materialName || 'Material desconhecido',
            materialCode: selection.materialCode || selection.rawMaterialCode || null,
            unit: selection.unit || 'Kg',
            lotNumber: selection.lotNumber || null,
            locationId: selection.locationId || null,
            expirationDate: selection.expirationDate || null,
            hasBatch: selection.hasBatch !== false && !!selection.batchId
        });
    });

    const validSelections = allSelections.filter(s => s.hasBatch && s.batchId);
    if (validSelections.length === 0) {
        console.log('Nenhuma seleção válida com lote para dedução.');
        return [];
    }

    console.log(`Deduzindo ${validSelections.length} materiais...`);
    const deductionResults = [];

    try {
        await runTransaction(db, async (transaction) => {
            const updates = [];
            for (const selection of validSelections) {
                const stockRef = doc(db, 'current-stock', selection.batchId);
                const stockDoc = await transaction.get(stockRef);
                if (!stockDoc.exists()) {
                    throw new Error(`Lote ${selection.lotNumber || 'desconhecido'} não encontrado no estoque.`);
                }
                const stockData = stockDoc.data();
                const currentQty = parseFloat(stockData.quantity) || 0;
                const amountToDeduct = selection.selectedQuantity;
                if (amountToDeduct > currentQty + 0.0001) {
                    throw new Error(
                        `Estoque insuficiente para ${selection.materialName} (Lote: ${selection.lotNumber || 'N/A'}). ` +
                        `Disponível: ${currentQty.toFixed(3)}, Solicitado: ${amountToDeduct.toFixed(3)}`
                    );
                }
                const newQty = parseFloat((currentQty - amountToDeduct).toFixed(4));
                updates.push({ ref: stockRef, newQty, stockData, selection });
            }
            for (const update of updates) {
                if (update.newQty <= 0.001) {
                    transaction.delete(update.ref);
                } else {
                    transaction.update(update.ref, {
                        quantity: update.newQty,
                        lastDeductedAt: serverTimestamp(),
                        lastDeductedBy: orderId,
                        orderCode: opCode
                    });
                }
                deductionResults.push({
                    materialId: update.stockData.rawMaterialId,
                    materialCode: update.stockData.rawMaterialCode || update.selection.materialCode,
                    materialName: update.stockData.rawMaterialName || update.selection.materialName,
                    quantity: update.selection.selectedQuantity,
                    unit: update.selection.unit || 'Kg',
                    locationId: update.stockData.locationId,
                    locationDescription: update.stockData.locationDescription,
                    stockDocId: update.ref.id,
                    stockCollection: 'current-stock',
                    lotNumber: update.stockData.lotNumber,
                    expirationDate: update.stockData.expirationDate,
                    batchSelected: update.selection.lotNumber
                });
            }
        });

        if (deductionResults.length > 0) {
            const outflowItems = deductionResults.map(r => ({
                rawMaterialId: r.materialId,
                rawMaterialCode: r.materialCode || r.materialId,
                rawMaterialName: r.materialName,
                quantity: r.quantity,
                unit: r.unit || 'Kg',
                locationId: r.locationId,
                locationDescription: r.locationDescription,
                lotNumber: r.lotNumber,
                stockDocId: r.stockDocId,
                expirationDate: r.expirationDate,
            }));

            const locationIds = [...new Set(deductionResults.filter(r => r.locationId).map(r => r.locationId))];
            const locationQuantities = {};
            deductionResults.forEach(r => {
                if (r.locationId) {
                    locationQuantities[r.locationId] = (locationQuantities[r.locationId] || 0) + r.quantity;
                }
            });

            const totalQuantity = deductionResults.reduce((sum, r) => sum + r.quantity, 0);
            const firstItem = deductionResults[0];

            await addDoc(collection(db, 'stock-outflows'), {
                rawMaterialId: firstItem.materialId,
                rawMaterialCode: firstItem.materialCode || firstItem.materialId,
                rawMaterialName: firstItem.materialName,
                locationIds: locationIds,
                locationQuantities: locationQuantities,
                quantity: parseFloat(totalQuantity.toFixed(4)),
                type: 'SAÍDA',
                notes: `Saída para produção - OP: ${opCode}`,
                createdAt: serverTimestamp(),
                createdBy: 'Sistema (Produção)',
                lotNumber: firstItem.lotNumber,
                expirationDate: firstItem.expirationDate,
                isProduct: false,
                orderId: orderId,
                opCode: opCode,
                items: outflowItems,
                status: 'COMPLETED',
                source: 'production'
            });
            console.log(`Saída registrada no stock-outflows: ${deductionResults.length} itens, total: ${totalQuantity.toFixed(4)}`);
        }

        console.log(`Dedução concluída: ${deductionResults.length} itens`);
        return deductionResults;
    } catch (error) {
        console.error("Erro na dedução de estoque:", error);
        throw error;
    }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ProductionPhasePage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('fases_de_producao');
    const { logActivity } = useActivityLogger();
    const { addNotification } = useNotifications();

    // State
    const [allOrders, setAllOrders] = useState([]);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [activeOrders, setActiveOrders] = useState([]);
    const [completedOrders, setCompletedOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [currentPhase, setCurrentPhase] = useState(null);
    const [allRawMaterials, setAllRawMaterials] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [actualStockMap, setActualStockMap] = useState({});
    const [products, setProducts] = useState([]);
    const [procedures, setProcedures] = useState([]);
    const [showMissingMaterialsModal, setShowMissingMaterialsModal] = useState(false);
    const [selectedMissingOrder, setSelectedMissingOrder] = useState(null);
    const [allLocations, setAllLocations] = useState([]);

    // Phase management state
    const [phaseProgress, setPhaseProgress] = useState({});
    const [phaseNotes, setPhaseNotes] = useState({});
    const [phaseWeighingData, setPhaseWeighingData] = useState({});

    // Debounce refs for notes
    const notesDebounceRef = useRef({});
    const pendingNotesRef = useRef({});

    // Pesagem (Phase 1) state
    const [pesagemRows, setPesagemRows] = useState({});
    const [showFloatModal, setShowFloatModal] = useState(false);
    const [floatMaterials, setFloatMaterials] = useState([]);
    const [floatForm, setFloatForm] = useState({ inciName: '', quantidade: '', obs: '' });

    // Batch selection state for Phase 1
    const [batchOptions, setBatchOptions] = useState({});
    const [batchSelections, setBatchSelections] = useState({});
    const [loadingBatches, setLoadingBatches] = useState({});

    // Phase 2 state
    const [fabricationDate, setFabricationDate] = useState('');
    const [expirationDate, setExpirationDate] = useState('');

    // Phase 5 & 6 state
    const [qualityResults, setQualityResults] = useState({});
    const [qualityStatusFilter, setQualityStatusFilter] = useState('all');

    // Phase 6 state
    const [phValue, setPhValue] = useState('');
    const [appearance, setAppearance] = useState('');
    const [qualityObservations, setQualityObservations] = useState('');

    // Phase 8 state
    const [funilSelecionado, setFunilSelecionado] = useState('');
    const [mangueiraSelecionada, setMangueiraSelecionada] = useState('');
    const [maquinaEnvaseSelecionada, setMaquinaEnvaseSelecionada] = useState('');
    const [allProcedures, setAllProcedures] = useState([]);

    // Phase 9 state
    const [envaseObservations, setEnvaseObservations] = useState('');

    // Phase 10 state
    const [rotulagemData, setRotulagemData] = useState({
        observacoesQualidade: '',
        quantidadeEmbalada: '',
        quantidadeCaixas: '',
        itensPorCaixa: '',
        conferenteSelo: '',
        conferenteLote: '',
        conferenteRetencao: '',
        location: '', // Add this
    });

    // Documents Modal state
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [selectedOrderForDocs, setSelectedOrderForDocs] = useState(null);

    // UI State
    const [isLoading, setIsLoading] = useState(false);
    const [isActivatingOrder, setIsActivatingOrder] = useState(null);
    const activatingRef = useRef(false);
    const [showPhaseDetail, setShowPhaseDetail] = useState(false);
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState('pending');

    // Toast and Confirm state
    const [toasts, setToasts] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState(null);

    // ============================================================================
    // TOAST AND CONFIRM HELPERS
    // ============================================================================
    const showToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(n => n.id !== id));
    }, []);

    const showConfirm = useCallback((message, onConfirm) => {
        setConfirmDialog({ message, onConfirm });
    }, []);

    const closeConfirm = useCallback(() => {
        setConfirmDialog(null);
    }, []);

    // ============================================================================
    // NOTIFICATION HELPERS
    // ============================================================================
    const sendPhaseNotification = useCallback((order, phaseId, phaseName, type, additionalData = {}) => {
        if (!order) return;

        const notificationData = {
            module: 'fases_de_producao', // Updated from 'ordens_de_producao'
            type: type,
            title: `⚙️ Fase de Produção - ${phaseName}`,
            message: `OP ${order.opCode}: ${phaseName} ${type === 'phase_completed' ? 'concluída' : type === 'phase_skipped' ? 'pulada' : 'iniciada'}`,
            timestamp: new Date().toISOString(),
            route: `/producao/fases-de-producao`,
            priority: 'Média',
            data: {
                orderId: order.id,
                opCode: order.opCode,
                phaseId: phaseId,
                phaseName: phaseName,
                ...additionalData
            },
            orderCode: order.opCode,
            phaseName: phaseName,
            status: type === 'phase_completed' ? 'completed' : type === 'phase_skipped' ? 'skipped' : 'started'
        };

        addNotification(notificationData);
    }, [addNotification]);

    const sendOrderActivationNotification = useCallback((order, availablePhases) => {
        if (!order) return;

        const notificationData = {
            module: 'ordens_de_producao',
            type: 'order_activated',
            title: '🏭 Ordem de Produção Ativada',
            message: `OP ${order.opCode} ativada com ${availablePhases.length} fases configuradas`,
            timestamp: new Date().toISOString(),
            route: `/producao/ordens-de-producao`,
            priority: 'Alta',
            data: {
                orderId: order.id,
                opCode: order.opCode,
                phaseCount: availablePhases.length,
                phases: availablePhases.map(p => p.id)
            },
            orderCode: order.opCode,
            status: 'active'
        };

        addNotification(notificationData);
    }, [addNotification]);

    const sendOrderCompletedNotification = useCallback((order) => {
        if (!order) return;

        const notificationData = {
            module: 'ordens_de_producao',
            type: 'order_completed',
            title: '✅ Ordem de Produção Concluída',
            message: `OP ${order.opCode} foi finalizada com sucesso`,
            timestamp: new Date().toISOString(),
            route: `/producao/ordens-de-producao`,
            priority: 'Alta',
            data: {
                orderId: order.id,
                opCode: order.opCode,
                completedAt: new Date().toISOString()
            },
            orderCode: order.opCode,
            status: 'completed'
        };

        addNotification(notificationData);
    }, [addNotification]);

    const sendStockAlertNotification = useCallback((order, missingMaterials) => {
        if (!order || !missingMaterials || missingMaterials.length === 0) return;

        const materialNames = missingMaterials.map(m => m.name).join(', ');
        const notificationData = {
            module: 'ordens_de_producao',
            type: 'stock_alert',
            title: '⚠️ Estoque Insuficiente para Produção',
            message: `OP ${order.opCode} aguardando materiais: ${materialNames}`,
            timestamp: new Date().toISOString(),
            route: `/producao/ordens-de-producao`,
            priority: 'Alta',
            data: {
                orderId: order.id,
                opCode: order.opCode,
                missingMaterials: missingMaterials
            },
            orderCode: order.opCode,
            status: 'awaiting-mp',
            missingMaterialCount: missingMaterials.length
        };

        addNotification(notificationData);
    }, [addNotification]);

    // ============================================================================
    // HANDLE BATCH SELECT
    // ============================================================================
    const handleBatchSelect = (materialId, batch, selectedQty, materialName, unit) => {
        setBatchSelections(prev => ({
            ...prev,
            [materialId]: {
                batchId: batch.stockId,
                lotNumber: batch.lotNumber,
                selectedQuantity: selectedQty,
                materialName: materialName,
                materialCode: batch.rawMaterialCode || null,
                unit: unit,
                locationId: batch.locationId,
                expirationDate: batch.expirationDate,
                hasBatch: true
            }
        }));
    };

    // ============================================================================
    // HANDLE NO BATCH MATERIAL
    // ============================================================================
    const handleNoBatchMaterial = (materialId, selectedQty, materialName, unit) => {
        setBatchSelections(prev => ({
            ...prev,
            [materialId]: {
                hasBatch: false,
                selectedQuantity: selectedQty,
                materialName: materialName,
                materialCode: null,
                unit: unit,
                batchId: null,
                lotNumber: null
            }
        }));
    };

    const getCurrentDateFormatted = () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const [currentDate] = useState(getCurrentDateFormatted());

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================
    const handleViewMissingMaterials = (order) => {
        setSelectedMissingOrder(order);
        setShowMissingMaterialsModal(true);
    };

    const displayDate = useCallback((dateStr) => {
        if (!dateStr) return '—';
        if (dateStr.includes('/')) return dateStr;
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
    }, []);

    const formatTimestamp = useCallback((timestamp) => {
        if (!timestamp?.toDate) {
            if (typeof timestamp === 'string' && timestamp.includes('/')) return timestamp;
            return '—';
        }
        const date = timestamp.toDate();
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }, []);

    // Função para obter as fases disponíveis baseadas no produto
    const getProductAvailablePhases = useCallback((order) => {
        if (!order?.items?.[0]?.productId) {
            return ALL_SYSTEM_PHASES;
        }

        const product = products.find(p => p.id === order.items[0].productId);
        if (!product?.technical?.productionPhases || product.technical.productionPhases.length === 0) {
            return ALL_SYSTEM_PHASES;
        }

        const selectedProductPhases = product.technical.productionPhases;
        const availableSystemPhases = [];

        selectedProductPhases.forEach(productPhase => {
            const systemPhases = PRODUCT_TO_SYSTEM_PHASES[productPhase];
            if (systemPhases) {
                systemPhases.forEach(phaseId => {
                    const phaseDefinition = ALL_SYSTEM_PHASES.find(p => p.id === phaseId);
                    if (phaseDefinition && !availableSystemPhases.find(p => p.id === phaseId)) {
                        availableSystemPhases.push(phaseDefinition);
                    }
                });
            }
        });

        availableSystemPhases.sort((a, b) => a.number - b.number);
        return availableSystemPhases.length > 0 ? availableSystemPhases : ALL_SYSTEM_PHASES;
    }, [products]);

    const requiresBatchTracking = (material) => {
        if (material.isProduct === true) return true;
        const batchRequiredCategories = ['PRODUTO_ACABADO', 'MATERIA_PRIMA_CONTROLADA', 'QUIMICO'];
        if (material.categoryIds) {
            return material.categoryIds.some(catId => batchRequiredCategories.includes(catId));
        }
        return false;
    };

    const updateSaleOrderStatusIfCompleted = async (saleOrderId, opCode) => {
        if (!saleOrderId) return false;

        try {
            const opsQuery = query(
                collection(db, PRODUCTION_ORDERS_COLLECTION),
                where('saleOrderId', '==', saleOrderId)
            );
            const opsSnapshot = await getDocs(opsQuery);
            const allOps = opsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (allOps.length === 0) return false;
            const allCompleted = allOps.every(op => op.status === 'completed');
            if (!allCompleted) return false;

            const saleOrderRef = doc(db, 'sales-orders', saleOrderId);
            const saleOrderSnap = await getDoc(saleOrderRef);
            if (!saleOrderSnap.exists()) {
                console.error(`Pedido de venda ${saleOrderId} não encontrado`);
                return false;
            }

            const saleOrderData = saleOrderSnap.data();
            const existingHistory = saleOrderData.trackingHistory || [];

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: 'Produção Finalizada',
                description: `Todas as ordens de produção vinculadas foram concluídas. Última OP: ${opCode}`,
                updatedBy: 'Sistema (Produção)'
            };

            await updateDoc(saleOrderRef, {
                status: 'production_completed',
                productionCompletedAt: new Date().toISOString(),
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: serverTimestamp()
            });

            showToast(t('pages.productionPhase.saleOrderUpdated'), 'success');
            return true;
        } catch (error) {
            console.error('Erro ao atualizar pedido de venda:', error);
            return false;
        }
    };

    // ============================================================================
    // FETCH BATCHES FOR MATERIAL
    // ============================================================================
    const fetchLocations = useCallback(async () => {
        try {
            const locationsSnapshot = await getDocs(collection(db, 'localizacoes'));
            const locationsList = locationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAllLocations(locationsList);
        } catch (error) {
            console.error("Error fetching locations:", error);
        }
    }, []);

    const fetchBatchesForMaterial = useCallback(async (materialId) => {
        if (!materialId) return [];

        setLoadingBatches(prev => ({ ...prev, [materialId]: true }));
        try {
            const q = query(
                collection(db, 'current-stock'),
                where('rawMaterialId', '==', materialId),
                where('quantity', '>', 0.001)
            );
            const snapshot = await getDocs(q);

            const batches = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                const isExpiredBatch = data.expirationDate && isExpired(data.expirationDate);
                batches.push({
                    stockId: doc.id,
                    lotNumber: data.lotNumber || null,
                    locationId: data.locationId,
                    locationDescription: data.locationDescription,
                    availableQuantity: data.quantity,
                    expirationDate: data.expirationDate,
                    manufacturerDate: data.manufacturerDate,
                    isExpired: isExpiredBatch,
                    rawMaterialCode: data.rawMaterialCode
                });
            });

            batches.sort((a, b) => {
                if (a.isExpired && !b.isExpired) return 1;
                if (!a.isExpired && b.isExpired) return -1;
                if (!a.isExpired && !b.isExpired && a.expirationDate && b.expirationDate) {
                    const dateA = a.expirationDate.toDate ? a.expirationDate.toDate() : new Date(a.expirationDate);
                    const dateB = b.expirationDate.toDate ? b.expirationDate.toDate() : new Date(b.expirationDate);
                    return dateA - dateB;
                }
                return 0;
            });

            return batches;
        } catch (error) {
            console.error("Erro ao buscar lotes:", error);
            return [];
        } finally {
            setLoadingBatches(prev => ({ ...prev, [materialId]: false }));
        }
    }, []);

    // ============================================================================
    // LOAD BATCH OPTIONS WHEN PHASE 1 OPENS
    // ============================================================================
    useEffect(() => {
        if (selectedOrder && currentPhase?.id === 'phase-1' && showPhaseDetail) {
            loadBatchOptionsForOrder();
        }
    }, [selectedOrder, currentPhase, showPhaseDetail]);

    const loadBatchOptionsForOrder = async () => {
        if (!selectedOrder) return;

        const materialsArray = selectedOrder.materialsRequired || [];
        const nonWaterMaterials = materialsArray.filter(m => !m.isWater && m.needed > 0);

        const batchOptionsMap = {};
        for (const material of nonWaterMaterials) {
            const batches = await fetchBatchesForMaterial(material.id);
            if (batches.length > 0) {
                batchOptionsMap[material.id] = batches;
            }
        }
        setBatchOptions(batchOptionsMap);
    };

    // ============================================================================
    // AUTO-POPULATE QUANTITY AND AUTO-SELECT BATCH FOR PHASE 1
    // ============================================================================
    useEffect(() => {
        if (!selectedOrder || currentPhase?.id !== 'phase-1' || !showPhaseDetail) return;

        const item = selectedOrder.items?.[0] || {};
        const quantidadeKg = item.mode === 'mass'
            ? parseFloat(item.quantity) || 0
            : (() => {
                const prod = products.find(p => p.id === item.productId);
                const gram = parseFloat(prod?.gramatura) || 0;
                const gramKg = (prod?.gramaturaUnit === 'g' || prod?.gramaturaUnit === 'ml') ? gram / 1000 : gram;
                return (parseFloat(item.quantity) || 0) * gramKg;
            })();

        const prod = products.find(p => p.id === item.productId);
        const formulaMaterials = prod?.rawMaterials || [];

        const excludedCategoryIds = allCategories
            .filter(c => ['EMBALAGEM', 'SERIGRAFIA'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        const newPesagemRows = {};
        let hasChanges = false;

        formulaMaterials
            .filter(mp => {
                const mpData = allRawMaterials.find(rm => rm.id === mp.id);
                if (!mpData) return true;
                if (!mpData.categoryIds || mpData.categoryIds.length === 0) return true;
                return !mpData.categoryIds.some(catId => excludedCategoryIds.includes(catId));
            })
            .forEach(mp => {
                const percentage = parseFloat(mp.quantity) || 0;
                const quantidadeSugeridaLote = parseFloat(((quantidadeKg * percentage) / 100).toFixed(4));
                const rowKey = `${selectedOrder.id}-${mp.id}`;
                const currentRow = pesagemRows[rowKey];
                const currentUsed = currentRow?.quantidadeUtilizada;

                if (!currentUsed || parseFloat(currentUsed) === 0) {
                    newPesagemRows[rowKey] = {
                        ...(currentRow || { correcao: '', obs: '' }),
                        quantidadeUtilizada: quantidadeSugeridaLote.toFixed(3),
                        quantidadeSugerida: quantidadeSugeridaLote
                    };
                    hasChanges = true;
                } else if (currentRow && !currentRow.quantidadeSugerida) {
                    newPesagemRows[rowKey] = {
                        ...currentRow,
                        quantidadeSugerida: quantidadeSugeridaLote
                    };
                    hasChanges = true;
                }
            });

        if (hasChanges) {
            setPesagemRows(prev => ({ ...prev, ...newPesagemRows }));
        }
    }, [selectedOrder, currentPhase, showPhaseDetail, products, allCategories, allRawMaterials]);

    // Auto-select batch when quantity is filled
    useEffect(() => {
        if (!selectedOrder || currentPhase?.id !== 'phase-1' || !showPhaseDetail) return;

        const materialsArray = selectedOrder.materialsRequired || [];
        const nonWaterMaterials = materialsArray.filter(m => !m.isWater && m.needed > 0);

        nonWaterMaterials.forEach(material => {
            const rowKey = `${selectedOrder.id}-${material.id}`;
            const rowState = pesagemRows[rowKey];
            const usedQty = parseFloat(rowState?.quantidadeUtilizada) || 0;
            const currentSelection = batchSelections[material.id];

            if (usedQty > 0 && !currentSelection) {
                const batches = batchOptions[material.id] || [];
                if (batches.length > 0) {
                    const bestBatch = batches.find(b => !b.isExpired) || batches[0];
                    if (bestBatch && bestBatch.availableQuantity >= usedQty - 0.0001) {
                        handleBatchSelect(material.id, bestBatch, usedQty, material.name, 'Kg');
                    }
                }
            }
        });
    }, [pesagemRows, batchOptions, selectedOrder, currentPhase, showPhaseDetail]);

    // ============================================================================
    // FETCH DATA
    // ============================================================================
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const mpSnapshot = await getDocs(collection(db, RAW_MATERIALS_COLLECTION));
            setAllRawMaterials(mpSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

            const catSnapshot = await getDocs(collection(db, CATEGORY_COLLECTION_PATH));
            setAllCategories(catSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

            const prodSnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
            setProducts(prodSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

            const procSnapshot = await getDocs(collection(db, PROCEDURES_COLLECTION));
            setAllProcedures(procSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

            // NEW: Fetch locations
            const locationsSnapshot = await getDocs(collection(db, 'localizacoes'));
            const locationsList = locationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAllLocations(locationsList);

            const stockTotals = {};
            for (const colName of STOCK_COLLECTIONS) {
                const stockSnapshot = await getDocs(collection(db, colName));
                stockSnapshot.docs.forEach(d => {
                    const data = d.data();
                    const rmId = data.rawMaterialId;
                    const qty = parseFloat(data.quantity) || 0;
                    if (rmId && qty > 0.0001) {
                        stockTotals[rmId] = (stockTotals[rmId] || 0) + qty;
                    }
                });
            }
            setActualStockMap(stockTotals);
        } catch (error) {
            console.error("Error fetching data:", error);
            showToast(t('pages.productionPhase.errors.loadData'), 'error', 0);
        } finally {
            setIsLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ============================================================================
    // REAL-TIME LISTENER FOR PRODUCTION ORDERS
    // ============================================================================
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, PRODUCTION_ORDERS_COLLECTION),
            (snap) => {
                try {
                    const allOrdersList = snap.docs
                        .map(d => {
                            const data = d.data();
                            if (!data.opCode || !data.items) return null;
                            return { id: d.id, ...data };
                        })
                        .filter(o => o !== null);

                    setAllOrders(allOrdersList);

                    const pending = allOrdersList.filter(o =>
                        o.status === 'awaiting-mp' || o.status === 'awaiting-start'
                    );
                    const active = allOrdersList.filter(o => o.status === 'active');
                    const completed = allOrdersList.filter(o => o.status === 'completed');

                    setPendingOrders(pending);
                    setActiveOrders(active);
                    setCompletedOrders(completed);
                } catch (error) {
                    console.error("Error processing orders:", error);
                }
            },
            (error) => {
                console.error("Listener error:", error);
                showToast(t('pages.productionPhase.errors.dbConnection'), 'error', 0);
            }
        );
        return () => unsubscribe();
    }, [showToast, t]);

    useEffect(() => {
        if (!selectedOrder || currentPhase?.id !== 'phase-5' || !showPhaseDetail) return;

        const product = products.find(p => p.id === selectedOrder?.items?.[0]?.productId);
        const criteriaFromProduct = product?.qualityCriteria || [];
        const savedResults = selectedOrder?.qualityResults || {};

        if (criteriaFromProduct.length > 0) {
            const initializedResults = {};
            let needsUpdate = false;

            criteriaFromProduct.forEach(criterion => {
                if (!savedResults[criterion.id]) {
                    initializedResults[criterion.id] = {
                        status: 'pending',
                        observation: '',
                        analysis: criterion.analysis,
                        method: criterion.method,
                        specification: criterion.specification || '',
                    };
                    needsUpdate = true;
                } else {
                    initializedResults[criterion.id] = {
                        ...savedResults[criterion.id],
                        analysis: criterion.analysis,
                        method: criterion.method,
                        specification: criterion.specification || '',
                    };
                }
            });

            if (needsUpdate) {
                setQualityResults(prev => ({ ...prev, ...initializedResults }));
            }
        }
    }, [selectedOrder, currentPhase, showPhaseDetail, products]);

    // ============================================================================
    // SMART DEBOUNCE FOR NOTES
    // ============================================================================
    const saveNoteToFirestore = useCallback(async (orderId, phaseId, note) => {
        if (!orderId || !phaseId) return;
        try {
            const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
            await updateDoc(orderRef, { [`phaseProgress.${phaseId}.notes`]: note });
        } catch (error) {
            console.error("Error saving note:", error);
        }
    }, []);

    const handleNoteChange = useCallback((orderId, phaseId, value) => {
        setPhaseNotes(prev => ({ ...prev, [phaseId]: value }));

        if (notesDebounceRef.current[phaseId]) {
            clearTimeout(notesDebounceRef.current[phaseId]);
        }

        pendingNotesRef.current[phaseId] = { orderId, phaseId, value };

        notesDebounceRef.current[phaseId] = setTimeout(() => {
            const pending = pendingNotesRef.current[phaseId];
            if (pending && pending.value === value) {
                saveNoteToFirestore(pending.orderId, pending.phaseId, pending.value);
                delete pendingNotesRef.current[phaseId];
            }
            delete notesDebounceRef.current[phaseId];
        }, 3000);
    }, [saveNoteToFirestore]);

    useEffect(() => {
        return () => {
            Object.values(notesDebounceRef.current).forEach(timeout => {
                if (timeout) clearTimeout(timeout);
            });
        };
    }, []);

    // ============================================================================
    // HANDLE DOCUMENT EXPORT
    // ============================================================================
    const handleDocumentExport = async (documentType) => {
        if (!selectedOrderForDocs) return;

        if (documentType === 'documento-unico') {
            await exportProductionDocument({
                order: selectedOrderForDocs,
                allRawMaterials,
                allCategories,
                allItems: [],
                allProducts: products,
                allProductTypes: [],
                showToast
            });
            setShowDocumentsModal(false);
            return;
        }

        // Mantém os outros documentos...
        switch (documentType) {
            case 'processo-manipulacao':
                await exportProcessoManipulacao({
                    order: selectedOrderForDocs,
                    allRawMaterials,
                    allCategories,
                    showToast
                });
                break;
            case 'ficha-pesagem':
                await exportProcessoEnvase({
                    order: selectedOrderForDocs,
                    allRawMaterials,
                    allCategories,
                    allProductTypes: [],
                    showToast
                });
                break;
            case 'processo-controle-qualidade':
                await exportProcessoControleQualidade({
                    order: selectedOrderForDocs,
                    allRawMaterials,
                    allCategories,
                    allProductTypes: [],
                    showToast
                });
                break;
            default:
                showToast(t('pages.productionPhase.documents.notRecognized'), 'warning');
        }
        setShowDocumentsModal(false);
    };

    const openDocumentsModal = (order) => {
        setSelectedOrderForDocs(order);
        setShowDocumentsModal(true);
    };

    // ============================================================================
    // PROCEDURE OPTIONS FOR DROPDOWNS
    // ============================================================================
    const funilOptions = useMemo(() => {
        return allProcedures
            .filter(p => p.name && p.name.toLowerCase().includes('funil'))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [allProcedures]);

    const mangueiraOptions = useMemo(() => {
        return allProcedures
            .filter(p => p.name && (p.name.toLowerCase().includes('bomba') || p.name.toLowerCase().includes('transferencia')))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [allProcedures]);

    const maquinaEnvaseOptions = useMemo(() => {
        return allProcedures
            .filter(p => p.name && (p.name.toLowerCase().includes('envase') || p.name.toLowerCase().includes('envasadora')))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [allProcedures]);

    // ============================================================================
    // GET CURRENT STOCK MAP
    // ============================================================================
    const getCurrentStockMap = useCallback(async () => {
        const stockTotals = {};
        for (const colName of STOCK_COLLECTIONS) {
            const stockSnapshot = await getDocs(collection(db, colName));
            stockSnapshot.docs.forEach(d => {
                const data = d.data();
                const rmId = data.rawMaterialId;
                const qty = parseFloat(data.quantity) || 0;
                if (rmId && qty > 0.0001) {
                    stockTotals[rmId] = (stockTotals[rmId] || 0) + qty;
                }
            });
        }
        return stockTotals;
    }, []);

    // ============================================================================
    // RECALCULATE MATERIALS NEEDED
    // ============================================================================
    const recalculateMaterialsNeeded = useCallback(async (orderData) => {
        const allMaterials = {};
        const unitCategoryIds = allCategories
            .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        for (const item of orderData.items || []) {
            const product = products.find(p => p.id === item.productId);
            if (!product) continue;

            const value = parseFloat(item.quantity);
            const materials = product.rawMaterials || [];
            const lossMultiplier = parseFloat(product.lossIndex) || 1;

            const gramaturaRaw = parseFloat(product.gramatura) || 0;
            const gramaturaBase = (product.gramaturaUnit === 'g' || product.gramaturaUnit === 'ml')
                ? gramaturaRaw / 1000
                : gramaturaRaw;

            const totalMass = item.mode === 'units' ? value * gramaturaBase : value;
            const totalUnits = item.mode === 'units' ? value : (gramaturaBase > 0 ? Math.floor(value / gramaturaBase) : 0);

            for (const mp of materials) {
                if (!mp.id) continue;
                const mpData = allRawMaterials.find(rm => rm.id === mp.id);
                const isUnit = mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
                const quantityFactor = parseFloat(mp.quantity) || 0;
                const isWater = isWaterMaterial(mpData);

                let needed;
                if (isUnit) {
                    needed = totalUnits * quantityFactor;
                } else {
                    const baseNeeded = (totalMass * quantityFactor) / 100;
                    needed = baseNeeded * lossMultiplier;
                }

                if (!allMaterials[mp.id]) {
                    allMaterials[mp.id] = {
                        id: mp.id,
                        needed: 0,
                        isWater,
                        unit: isUnit ? 'Un' : ((product.gramaturaUnit === 'ml' || product.gramaturaUnit === 'L') ? 'L' : 'Kg'),
                        name: mp.inciName || mp.label || mpData?.commercialName || mp.id
                    };
                }
                allMaterials[mp.id].needed += needed;
            }
        }
        return Object.values(allMaterials).filter(m => m.needed > 0.0001);
    }, [allCategories, allRawMaterials, products]);

    // ============================================================================
    // LOAD PHASE DATA FROM ORDER
    // ============================================================================
    useEffect(() => {
        if (selectedOrder && selectedOrder.phase8Data) {
            setFunilSelecionado(selectedOrder.phase8Data.funil || '');
            setMangueiraSelecionada(selectedOrder.phase8Data.mangueira || '');
            setMaquinaEnvaseSelecionada(selectedOrder.phase8Data.maquinaEnvase || '');
        } else {
            setFunilSelecionado('');
            setMangueiraSelecionada('');
            setMaquinaEnvaseSelecionada('');
        }
    }, [selectedOrder]);

    useEffect(() => {
        if (selectedOrder && selectedOrder.phase9Data) {
            setEnvaseObservations(selectedOrder.phase9Data.observations || '');
        } else {
            setEnvaseObservations('');
        }
    }, [selectedOrder]);

    useEffect(() => {
        if (selectedOrder && selectedOrder.phase10Data) {
            setRotulagemData({
                observacoesQualidade: selectedOrder.phase10Data.observacoesQualidade || '',
                quantidadeEmbalada: selectedOrder.phase10Data.quantidadeEmbalada || '',
                quantidadeCaixas: selectedOrder.phase10Data.quantidadeCaixas || '',
                itensPorCaixa: selectedOrder.phase10Data.itensPorCaixa || '',
                conferenteSelo: selectedOrder.phase10Data.conferenteSelo || '',
                conferenteLote: selectedOrder.phase10Data.conferenteLote || '',
                conferenteRetencao: selectedOrder.phase10Data.conferenteRetencao || '',
            });
        } else {
            setRotulagemData({
                observacoesQualidade: '',
                quantidadeEmbalada: '',
                quantidadeCaixas: '',
                itensPorCaixa: '',
                conferenteSelo: '',
                conferenteLote: '',
                conferenteRetencao: '',
            });
        }
    }, [selectedOrder]);

    // ============================================================================
    // AUTO-CALCULATE FOR PHASE 10
    // ============================================================================
    useEffect(() => {
        if (!selectedOrder || !showPhaseDetail || currentPhase?.id !== 'phase-10') return;

        const quantidadeCaixasNum = parseFloat(rotulagemData.quantidadeCaixas?.replace(',', '.') || 0);
        const itensPorCaixaNum = parseFloat(rotulagemData.itensPorCaixa?.replace(',', '.') || 0);
        const quantidadeEmbaladaNum = parseFloat(rotulagemData.quantidadeEmbalada?.replace(',', '.') || 0);

        const hasCaixas = !isNaN(quantidadeCaixasNum) && quantidadeCaixasNum > 0;
        const hasItens = !isNaN(itensPorCaixaNum) && itensPorCaixaNum > 0;
        const hasEmbalada = !isNaN(quantidadeEmbaladaNum) && quantidadeEmbaladaNum > 0;

        const filledCount = [hasCaixas, hasItens, hasEmbalada].filter(Boolean).length;

        if (filledCount >= 2) {
            let newState = { ...rotulagemData };
            let updated = false;

            if (hasCaixas && hasItens && !hasEmbalada) {
                const calculated = (quantidadeCaixasNum * itensPorCaixaNum).toFixed(3).replace('.', ',');
                if (rotulagemData.quantidadeEmbalada !== calculated) {
                    newState.quantidadeEmbalada = calculated;
                    updated = true;
                }
            } else if (hasEmbalada && hasItens && !hasCaixas && itensPorCaixaNum > 0) {
                const calculated = (quantidadeEmbaladaNum / itensPorCaixaNum).toFixed(3).replace('.', ',');
                if (rotulagemData.quantidadeCaixas !== calculated) {
                    newState.quantidadeCaixas = calculated;
                    updated = true;
                }
            } else if (hasEmbalada && hasCaixas && !hasItens && quantidadeCaixasNum > 0) {
                const calculated = (quantidadeEmbaladaNum / quantidadeCaixasNum).toFixed(3).replace('.', ',');
                if (rotulagemData.itensPorCaixa !== calculated) {
                    newState.itensPorCaixa = calculated;
                    updated = true;
                }
            }

            if (updated) {
                setRotulagemData(newState);
            }
        }
    }, [rotulagemData.quantidadeCaixas, rotulagemData.itensPorCaixa, rotulagemData.quantidadeEmbalada, selectedOrder, showPhaseDetail, currentPhase]);

    // ============================================================================
    // BATCH WRITE FOR PHASE TRANSITIONS
    // ============================================================================
    const completePhaseWithBatch = async (orderId, phaseId, updateData, historyEntry) => {
        const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
        const batch = writeBatch(db);
        const finalUpdateData = { ...updateData };
        if (historyEntry) {
            finalUpdateData.phaseHistory = arrayUnion(historyEntry);
        }
        batch.update(orderRef, finalUpdateData);
        await batch.commit();
    };

    // ============================================================================
    // GET PHASE NAME
    // ============================================================================
    const getPhaseName = (phaseId) => {
        return PHASE_DISPLAY_NAMES[phaseId] || phaseId;
    };

    const getPhaseStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#27ae60';
            case 'in-progress': return '#3498db';
            case 'pending': return '#95a5a6';
            case 'skipped': return '#f39c12';
            default: return '#95a5a6';
        }
    };

    // ============================================================================
    // COMPLETE PHASE 1 WITH BATCH SELECTIONS
    // ============================================================================
    const handleCompletePhase1WithBatches = async () => {
        if (!selectedOrder) {
            showToast('Nenhuma ordem selecionada.', 'error');
            return;
        }

        const formulaMaterials = selectedOrder.items?.[0]?.productId
            ? products.find(p => p.id === selectedOrder.items[0].productId)?.rawMaterials || []
            : [];

        const missingMaterials = [];
        for (const mp of formulaMaterials) {
            const rowKey = `${selectedOrder.id}-${mp.id}`;
            const rowState = pesagemRows[rowKey];
            const usedQty = parseFloat(rowState?.quantidadeUtilizada) || 0;
            if (usedQty <= 0) {
                const mpData = allRawMaterials.find(rm => rm.id === mp.id);
                missingMaterials.push(mpData?.commercialName || mp.inciName || mp.id);
            }
        }

        if (missingMaterials.length > 0) {
            showToast(`Preencha as quantidades para: ${missingMaterials.join(', ')}`, 'error');
            return;
        }

        try {
            const selectionsForDeduction = {};
            let hasSelectionsToDeduct = false;

            for (const mp of formulaMaterials) {
                const rowKey = `${selectedOrder.id}-${mp.id}`;
                const rowState = pesagemRows[rowKey];
                const usedQty = parseFloat(rowState?.quantidadeUtilizada) || 0;
                if (usedQty <= 0) continue;

                const mpData = allRawMaterials.find(rm => rm.id === mp.id);
                const isWater = isWaterMaterial(mpData);
                if (isWater) continue;

                const requiresBatch = mpData ? requiresBatchTracking(mpData) : false;

                if (requiresBatch) {
                    const selection = batchSelections[mp.id];
                    if (selection && selection.hasBatch !== false && selection.batchId) {
                        selectionsForDeduction[mp.id] = { ...selection, selectedQuantity: usedQty };
                        hasSelectionsToDeduct = true;
                    } else {
                        showToast(`Selecione um lote para: ${mpData?.commercialName || mp.inciName || mp.id}`, 'error');
                        return;
                    }
                } else {
                    const selection = batchSelections[mp.id];
                    if (selection && selection.hasBatch !== false && selection.batchId) {
                        selectionsForDeduction[mp.id] = { ...selection, selectedQuantity: usedQty };
                        hasSelectionsToDeduct = true;
                    } else {
                        const batches = batchOptions[mp.id] || [];
                        if (batches.length > 0) {
                            const bestBatch = batches.find(b => !b.isExpired) || batches[0];
                            if (bestBatch && bestBatch.availableQuantity >= usedQty - 0.0001) {
                                selectionsForDeduction[mp.id] = {
                                    batchId: bestBatch.stockId,
                                    lotNumber: bestBatch.lotNumber,
                                    selectedQuantity: usedQty,
                                    materialName: mpData?.commercialName || mp.inciName || mp.id,
                                    materialCode: mpData?.code || null,
                                    unit: 'Kg',
                                    locationId: bestBatch.locationId,
                                    expirationDate: bestBatch.expirationDate,
                                    hasBatch: true
                                };
                                hasSelectionsToDeduct = true;
                            }
                        }
                    }
                }
            }

            for (const fm of floatMaterials) {
                const qty = parseFloat(fm.quantidade) || 0;
                if (qty <= 0) continue;
                const material = allRawMaterials.find(m =>
                    m.inciName === fm.inciName || m.commercialName === fm.inciName
                );
                if (material) {
                    const isWater = isWaterMaterial(material);
                    if (!isWater) {
                        const batches = batchOptions[material.id] || [];
                        if (batches.length > 0) {
                            const bestBatch = batches.find(b => !b.isExpired) || batches[0];
                            if (bestBatch && bestBatch.availableQuantity >= qty - 0.0001) {
                                selectionsForDeduction[`float-${Date.now()}-${fm.inciName}`] = {
                                    batchId: bestBatch.stockId,
                                    lotNumber: bestBatch.lotNumber,
                                    selectedQuantity: qty,
                                    materialName: material.commercialName || material.inciName || material.id,
                                    materialCode: material.code || null,
                                    unit: 'Kg',
                                    locationId: bestBatch.locationId,
                                    expirationDate: bestBatch.expirationDate,
                                    hasBatch: true
                                };
                                hasSelectionsToDeduct = true;
                            }
                        }
                    }
                }
            }

            let deductionLog = [];
            if (hasSelectionsToDeduct && Object.keys(selectionsForDeduction).length > 0) {
                try {
                    deductionLog = await executeAtomicStockDeductionWithBatches(
                        selectionsForDeduction,
                        selectedOrder.id,
                        selectedOrder.opCode
                    );
                } catch (deductionError) {
                    console.error('Erro na dedução de estoque:', deductionError);
                    showToast(`Erro ao deduzir estoque: ${deductionError.message}`, 'error', 8000);
                    return;
                }
            } else {
                showToast('Nenhum material com lote selecionado para dedução.', 'warning', 4000);
            }

            const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, selectedOrder.id);
            const orderSnap = await getDoc(orderRef);
            const orderData = orderSnap.data();

            const updatedPhaseProgress = { ...orderData.phaseProgress };
            const activePhaseIds = orderData.activePhases || Object.keys(updatedPhaseProgress);
            const currentPhaseIndex = activePhaseIds.indexOf('phase-1');
            const nextPhaseId = activePhaseIds[currentPhaseIndex + 1];

            updatedPhaseProgress['phase-1'] = {
                ...updatedPhaseProgress['phase-1'],
                status: 'completed',
                completedAt: new Date().toISOString(),
                notes: phaseNotes['phase-1'] || '',
                dwellTimeMinutes: 0
            };

            if (nextPhaseId) {
                updatedPhaseProgress[nextPhaseId] = {
                    ...updatedPhaseProgress[nextPhaseId],
                    status: 'in-progress',
                    startedAt: new Date().toISOString(),
                };
            }

            const historyEntry = {
                phaseId: 'phase-1',
                phaseName: getPhaseName('phase-1'),
                completedAt: new Date().toISOString(),
                notes: phaseNotes['phase-1'] || '',
                batchSelections: selectionsForDeduction,
                deductionLog: deductionLog,
                pesagemRows: pesagemRows,
                floatMaterials: floatMaterials
            };

            const updateData = {
                phaseProgress: updatedPhaseProgress,
                currentPhase: nextPhaseId || 'phase-1',
                deductionLog: deductionLog,
                pesagemData: pesagemRows,
                floatMaterials: floatMaterials,
                batchSelections: selectionsForDeduction,
                materialsWithoutBatch: Object.entries(batchSelections)
                    .filter(([_, selection]) => selection.hasBatch === false)
                    .map(([materialId, selection]) => ({
                        materialId,
                        materialName: selection.materialName,
                        quantity: selection.selectedQuantity
                    }))
            };

            const allCompleted = activePhaseIds.every(phaseId =>
                updatedPhaseProgress[phaseId]?.status === 'completed' ||
                updatedPhaseProgress[phaseId]?.status === 'skipped'
            );

            if (allCompleted) {
                updateData.status = 'completed';
                updateData.completedAt = new Date().toISOString();

                // Add finished product to stock
                try {
                    const updatedOrderData = { ...orderData, ...updateData };
                    await addFinishedProductToStock(selectedOrder.id, updatedOrderData);
                } catch (error) {
                    console.error('Erro ao adicionar produto ao estoque:', error);
                    showToast(`⚠️ Ordem concluída, mas erro ao adicionar produto ao estoque: ${error.message}`, 'warning', 6000);
                }

                if (orderData.saleOrderId) {
                    try {
                        await updateSaleOrderStatusIfCompleted(orderData.saleOrderId, orderData.opCode);
                    } catch (err) {
                        console.error('Erro ao atualizar pedido de venda:', err);
                    }
                }
            }

            const batch = writeBatch(db);
            batch.update(orderRef, updateData);
            batch.update(orderRef, { phaseHistory: arrayUnion(historyEntry) });
            await batch.commit();

            await logActivity(
                'phase_completed',
                `production-order/${selectedOrder.id}/phase/phase-1`,
                `Fase 1 (Pesagem) concluída para OP ${selectedOrder.opCode} - ${deductionCount} materiais deduzidos`
            );

            const deductionCount = deductionLog.length;
            const materialsDeducted = deductionLog.map(d => d.materialName).join(', ');

            // Send notification for phase completion
            sendPhaseNotification(selectedOrder, 'phase-1', 'Pesagem', 'phase_completed', {
                deductionCount,
                materialsDeducted
            });

            if (allCompleted) {
                sendOrderCompletedNotification(selectedOrder);
            }

            if (deductionCount > 0) {
                showToast(`✅ Fase 1 concluída! ${deductionCount} materiais deduzidos: ${materialsDeducted}`, 'success', 6000);
            } else {
                showToast('✅ Fase 1 concluída! Nenhum material com lote foi deduzido (água ou materiais sem lote).', 'success', 4000);
            }

            setPesagemRows({});
            setFloatMaterials([]);
            setBatchSelections({});
            setBatchOptions({});
            setPhaseNotes(prev => ({ ...prev, 'phase-1': '' }));
            setShowPhaseDetail(false);
            setSelectedOrder(null);
            await fetchData();

        } catch (error) {
            console.error('Erro ao concluir fase 1:', error);
            showToast(`Erro ao concluir fase: ${error.message}`, 'error', 8000);
        }
    };

    // ============================================================================
    // ACTIVATE ORDER
    // ============================================================================
    const handleActivateOrder = async (orderId) => {
        if (activatingRef.current) return;
        activatingRef.current = true;
        setIsActivatingOrder(orderId);

        try {
            const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
            const orderSnap = await getDoc(orderRef);
            const orderData = orderSnap.data();

            if (!orderData) throw new Error("Order not found");

            if (orderData.status === 'active') {
                showToast(t('pages.productionPhase.activation.alreadyActive'), 'info');
                return;
            }

            if (orderData.status === 'awaiting-start') {
                const orderWithProduct = { ...orderData, id: orderId };
                const availablePhases = getProductAvailablePhases(orderWithProduct);

                const initialPhaseProgress = {};
                availablePhases.forEach((phase, index) => {
                    initialPhaseProgress[phase.id] = {
                        status: index === 0 ? 'in-progress' : 'pending',
                        startedAt: index === 0 ? new Date().toISOString() : null,
                        completedAt: null,
                        completedBy: null,
                        notes: ''
                    };
                });

                await updateDoc(orderRef, {
                    status: 'active',
                    activePhases: availablePhases.map(p => p.id),
                    phaseProgress: initialPhaseProgress,
                    currentPhase: availablePhases[0]?.id || null,
                    phaseHistory: [],
                    activatedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });

                // Send activation notification
                sendOrderActivationNotification({ ...orderData, id: orderId }, availablePhases);

                showToast(
                    t('pages.productionPhase.activation.started', {
                        opCode: orderData.opCode,
                        phaseName: PHASE_DISPLAY_NAMES[availablePhases[0]?.id] || availablePhases[0]?.name || 'Pesagem'
                    }),
                    'success'
                );

                await fetchData();
                setShowActivationModal(false);
                return;
            }

            let materialsArray = orderData.materialsRequired || [];
            if (materialsArray.length === 0) {
                materialsArray = await recalculateMaterialsNeeded(orderData);
            }

            const liveStockMap = await getCurrentStockMap();
            const nonWaterMaterials = materialsArray.filter(m => !m.isWater);

            const missingMaterials = nonWaterMaterials.filter(
                m => m.needed > (liveStockMap[m.id] || 0)
            );

            if (missingMaterials.length > 0) {
                await updateDoc(orderRef, {
                    status: 'awaiting-mp',
                    missingMaterials: missingMaterials.map(m => ({
                        id: m.id,
                        name: m.name,
                        needed: m.needed,
                        available: liveStockMap[m.id] || 0,
                        unit: m.unit
                    })),
                    updatedAt: serverTimestamp(),
                });

                await logActivity(
                    'order_activated',
                    `production-order/${orderId}`,
                    `OP ${orderData.opCode} ativada - ${availablePhases.length} fases configuradas`
                );

                // Send stock alert notification
                sendStockAlertNotification({ ...orderData, id: orderId }, missingMaterials);

                showToast(
                    t('pages.productionPhase.activation.insufficientStock', { opCode: orderData.opCode }),
                    'warning',
                    5000
                );
                await fetchData();
                return;
            }

            const orderWithProduct = { ...orderData, id: orderId };
            const availablePhases = getProductAvailablePhases(orderWithProduct);

            const initialPhaseProgress = {};
            availablePhases.forEach((phase, index) => {
                initialPhaseProgress[phase.id] = {
                    status: index === 0 ? 'in-progress' : 'pending',
                    startedAt: index === 0 ? new Date().toISOString() : null,
                    completedAt: null,
                    completedBy: null,
                    notes: ''
                };
            });

            await updateDoc(orderRef, {
                materialsRequired: materialsArray,
                status: 'active',
                activePhases: availablePhases.map(p => p.id),
                phaseProgress: initialPhaseProgress,
                currentPhase: availablePhases[0]?.id || null,
                phaseHistory: [],
                activatedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            // Send activation notification
            sendOrderActivationNotification({ ...orderData, id: orderId }, availablePhases);

            showToast(
                t('pages.productionPhase.activation.phasesConfigured', {
                    opCode: orderData.opCode,
                    count: availablePhases.length,
                    total: ALL_SYSTEM_PHASES.length
                }),
                'success'
            );

            await fetchData();
            setShowActivationModal(false);

        } catch (error) {
            console.error("Error activating order:", error);
            showToast(`${t('pages.productionPhase.errors.activateOrderError')} ${error.message}`, 'error', 0);
        } finally {
            activatingRef.current = false;
            setIsActivatingOrder(null);
        }
    };

    // ============================================================================
    // HANDLE PHASE COMPLETION (Other phases)
    // ============================================================================
    const handleCompletePhase = async (orderId, phaseId) => {
        if (phaseId === 'phase-2' && !fabricationDate) {
            showToast(t('pages.productionPhase.validation.fabricationDateRequired'), 'error');
            return;
        }

        if (phaseId === 'phase-5') {
            const product = products.find(p => p.id === selectedOrder?.items?.[0]?.productId);
            const qualityCriteria = product?.qualityCriteria || [];

            if (qualityCriteria.length > 0) {
                // Check if all criteria have been evaluated
                const evaluatedResults = Object.values(qualityResults);
                const pendingCount = evaluatedResults.filter(r => r?.status === 'pending').length;

                if (pendingCount > 0) {
                    showToast(`${pendingCount} ${t('pages.productionPhase.validation.criteriaPending')}`, 'error');
                    return;
                }

                // Check if any are rejected
                const hasRejected = evaluatedResults.some(r => r?.status === 'rejected');
                if (hasRejected) {
                    showToast(t('pages.productionPhase.validation.criteriaRejected'), 'error');
                    return;
                }

                // Check if all are approved (or restricted with special handling)
                const hasApproved = evaluatedResults.every(r => r?.status === 'approved' || r?.status === 'restricted');
                if (!hasApproved) {
                    showToast(t('pages.productionPhase.validation.allCriteriaRequired'), 'error');
                    return;
                }
            }
        }

        if (phaseId === 'phase-6') {
            const product = products.find(p => p.id === selectedOrder?.items?.[0]?.productId);
            const qualityCriteria = product?.qualityCriteria || [];
            const savedResultsData = selectedOrder?.qualityResults || qualityResults;
            const hasRejected = Object.values(savedResultsData).some(r => r.status === 'rejected');

            if (hasRejected) {
                showToast(t('pages.productionPhase.validation.cannotReleaseWithRejected'), 'error');
                return;
            }

            if (qualityCriteria.length > 0 && !phValue) {
                showToast(t('pages.productionPhase.validation.phRequired'), 'error');
                return;
            }

            if (qualityCriteria.length > 0 && !appearance) {
                showToast(t('pages.productionPhase.validation.appearanceRequired'), 'error');
                return;
            }
        }

        if (phaseId === 'phase-8') {
            if (!funilSelecionado) {
                showToast(t('pages.productionPhase.validation.funilRequired'), 'error');
                return;
            }
            if (!mangueiraSelecionada) {
                showToast(t('pages.productionPhase.validation.mangueiraRequired'), 'error');
                return;
            }
            if (!maquinaEnvaseSelecionada) {
                showToast(t('pages.productionPhase.validation.maquinaEnvaseRequired'), 'error');
                return;
            }
        }

        if (phaseId === 'phase-10') {
            if (!rotulagemData.quantidadeCaixas && !rotulagemData.quantidadeEmbalada) {
                showToast(t('pages.productionPhase.validation.quantidadeRequired'), 'error');
                return;
            }
            // NEW: Validate location is selected for Phase 10
            if (!rotulagemData.location) {
                showToast(t('pages.productionPhase.validation.locationRequired', 'Selecione a localização de destino do produto acabado.'), 'error');
                return;
            }
        }

        showConfirm(
            `${t('pages.productionPhase.confirmComplete')} ${getPhaseName(phaseId)}?`,
            async () => {
                try {
                    const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
                    const orderSnap = await getDoc(orderRef);
                    const orderData = orderSnap.data();

                    const updatedPhaseProgress = { ...orderData.phaseProgress };
                    const activePhaseIds = orderData.activePhases || Object.keys(updatedPhaseProgress);
                    const currentPhaseIndex = activePhaseIds.indexOf(phaseId);
                    const nextPhaseId = activePhaseIds[currentPhaseIndex + 1];

                    const savedNote = phaseNotes[phaseId] || '';

                    const phaseStartTime = updatedPhaseProgress[phaseId]?.startedAt;
                    let dwellTimeMinutes = null;
                    if (phaseStartTime) {
                        const startDate = new Date(phaseStartTime);
                        const endDate = new Date();
                        dwellTimeMinutes = (endDate - startDate) / 1000 / 60;
                    }

                    updatedPhaseProgress[phaseId] = {
                        ...updatedPhaseProgress[phaseId],
                        status: 'completed',
                        completedAt: new Date().toISOString(),
                        notes: savedNote,
                        dwellTimeMinutes: dwellTimeMinutes
                    };

                    if (nextPhaseId) {
                        updatedPhaseProgress[nextPhaseId] = {
                            ...updatedPhaseProgress[nextPhaseId],
                            status: 'in-progress',
                            startedAt: new Date().toISOString(),
                        };
                    }

                    // Create history entry
                    const historyEntry = {
                        phaseId,
                        phaseName: getPhaseName(phaseId),
                        completedAt: new Date().toISOString(),
                        notes: savedNote,
                        dwellTimeMinutes: dwellTimeMinutes
                    };

                    const updateData = {
                        phaseProgress: updatedPhaseProgress,
                        currentPhase: nextPhaseId || phaseId,
                    };

                    if (dwellTimeMinutes !== null) {
                        updateData[`phaseMetrics.${phaseId}`] = {
                            dwellTimeMinutes,
                            completedAt: new Date().toISOString(),
                            phaseName: getPhaseName(phaseId)
                        };
                    }

                    if (phaseId === 'phase-2') {
                        updateData.fabricationDate = fabricationDate || null;
                        updateData.expirationDate = expirationDate || null;
                        historyEntry.fabricationDate = fabricationDate ? displayDate(fabricationDate) : null;
                        historyEntry.expirationDate = expirationDate || null;
                    }

                    if (phaseId === 'phase-3') {
                        updateData.productionCompletedAt = getCurrentDateFormatted();
                        historyEntry.productionCompletedAt = getCurrentDateFormatted();
                    }

                    if (phaseId === 'phase-4') {
                        updateData.qualityControlStartedAt = new Date().toISOString();
                    }

                    if (phaseId === 'phase-5') {
                        updateData.qualityResults = qualityResults;
                        updateData.qualityControlCompletedAt = new Date().toISOString();
                        historyEntry.qualityResults = qualityResults;
                    }

                    if (phaseId === 'phase-6') {
                        updateData.phase6Data = {
                            ph: phValue,
                            appearance: appearance,
                            observations: qualityObservations,
                            releasedAt: new Date().toISOString(),
                            releasedBy: 'production_team'
                        };
                        historyEntry.ph = phValue;
                        historyEntry.appearance = appearance;
                        historyEntry.qualityObservations = qualityObservations;
                    }

                    if (phaseId === 'phase-8') {
                        updateData.phase8Data = {
                            funil: funilSelecionado,
                            mangueira: mangueiraSelecionada,
                            maquinaEnvase: maquinaEnvaseSelecionada,
                            startedAt: new Date().toISOString()
                        };
                        historyEntry.funil = funilSelecionado;
                        historyEntry.mangueira = mangueiraSelecionada;
                        historyEntry.maquinaEnvase = maquinaEnvaseSelecionada;
                    }

                    if (phaseId === 'phase-9') {
                        updateData.phase9Data = {
                            observations: envaseObservations,
                            completedAt: new Date().toISOString()
                        };
                        historyEntry.envaseObservations = envaseObservations;
                    }

                    // FIXED: Phase 10 with location support
                    if (phaseId === 'phase-10') {
                        const calculatedTotal = (parseFloat(rotulagemData.quantidadeCaixas) || 0) * (parseFloat(rotulagemData.itensPorCaixa) || 0);

                        updateData.phase10Data = {
                            ...rotulagemData,
                            totalCalculado: calculatedTotal,
                            completedAt: new Date().toISOString(),
                            completedBy: 'production_team',
                            location: rotulagemData.location // Save the location
                        };

                        // Also save location at order level for easy access
                        updateData.finishedProductLocation = rotulagemData.location;

                        historyEntry.observacoesQualidade = rotulagemData.observacoesQualidade;
                        historyEntry.quantidadeEmbalada = rotulagemData.quantidadeEmbalada;
                        historyEntry.quantidadeCaixas = rotulagemData.quantidadeCaixas;
                        historyEntry.itensPorCaixa = rotulagemData.itensPorCaixa;
                        historyEntry.conferenteSelo = rotulagemData.conferenteSelo;
                        historyEntry.conferenteLote = rotulagemData.conferenteLote;
                        historyEntry.conferenteRetencao = rotulagemData.conferenteRetencao;
                        historyEntry.totalCalculado = calculatedTotal;
                        historyEntry.location = rotulagemData.location; // Add location to history
                    }

                    const allCompleted = activePhaseIds.every(phaseId =>
                        updatedPhaseProgress[phaseId]?.status === 'completed' ||
                        updatedPhaseProgress[phaseId]?.status === 'skipped'
                    );

                    if (allCompleted) {
                        updateData.status = 'completed';
                        updateData.completedAt = new Date().toISOString();

                        // Add finished product to stock - pass the location from phase10
                        try {
                            // Merge the order data with the location from phase10
                            const updatedOrderData = {
                                ...orderData,
                                ...updateData,
                                // Ensure location is passed to the stock function
                                finishedProductLocation: rotulagemData.location || orderData.finishedProductLocation
                            };
                            await addFinishedProductToStock(selectedOrder.id, updatedOrderData);
                            showToast(`✅ Produto finalizado adicionado ao estoque!`, 'success', 4000);
                        } catch (error) {
                            console.error('Erro ao adicionar produto ao estoque:', error);
                            showToast(`⚠️ Ordem concluída, mas erro ao adicionar produto ao estoque: ${error.message}`, 'warning', 6000);
                        }

                        if (orderData.saleOrderId) {
                            try {
                                await updateSaleOrderStatusIfCompleted(orderData.saleOrderId, orderData.opCode);
                            } catch (err) {
                                console.error('Erro ao atualizar pedido de venda:', err);
                            }
                        }
                    }

                    const batch = writeBatch(db);
                    batch.update(orderRef, updateData);
                    batch.update(orderRef, { phaseHistory: arrayUnion(historyEntry) });
                    await batch.commit();

                    await logActivity(
                        'phase_completed',
                        `production-order/${orderId}/phase/${phaseId}`,
                        `Fase ${getPhaseName(phaseId)} concluída para OP ${orderData.opCode}`
                    );

                    // Send notification for phase completion
                    sendPhaseNotification(
                        { ...orderData, id: orderId },
                        phaseId,
                        getPhaseName(phaseId),
                        'phase_completed',
                        { dwellTimeMinutes, nextPhaseId }
                    );

                    if (allCompleted) {
                        sendOrderCompletedNotification({ ...orderData, id: orderId });
                    }

                    showToast(`${t('pages.productionPhase.phaseCompleted')} ${getPhaseName(phaseId)}!`, 'success');

                    setPhaseNotes(prev => ({ ...prev, [phaseId]: '' }));

                    if (notesDebounceRef.current[phaseId]) {
                        clearTimeout(notesDebounceRef.current[phaseId]);
                        delete notesDebounceRef.current[phaseId];
                    }

                    if (phaseId === 'phase-2') {
                        setFabricationDate('');
                        setExpirationDate('');
                    }

                    if (phaseId === 'phase-5') {
                        setQualityResults({});
                        setQualityStatusFilter('all');
                    }

                    if (phaseId === 'phase-6') {
                        setPhValue('');
                        setAppearance('');
                        setQualityObservations('');
                    }

                    if (phaseId === 'phase-10') {
                        setRotulagemData({
                            observacoesQualidade: '',
                            quantidadeEmbalada: '',
                            quantidadeCaixas: '',
                            itensPorCaixa: '',
                            conferenteSelo: '',
                            conferenteLote: '',
                            conferenteRetencao: '',
                            location: '', // Reset location
                        });
                    }

                    setSelectedOrder(null);
                    setShowPhaseDetail(false);
                    await fetchData();

                } catch (error) {
                    console.error("Error completing phase:", error);
                    showToast(t('pages.productionPhase.errors.completePhaseError'), 'error');
                }
            }
        );
    };

    // ============================================================================
    // HANDLE SKIP PHASE
    // ============================================================================
    const handleSkipPhase = async (orderId, phaseId) => {
        showConfirm(
            `${t('pages.productionPhase.confirmSkip')} ${getPhaseName(phaseId)}?`,
            async () => {
                try {
                    const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
                    const orderSnap = await getDoc(orderRef);
                    const orderData = orderSnap.data();

                    const updatedPhaseProgress = { ...orderData.phaseProgress };
                    const activePhaseIds = orderData.activePhases || Object.keys(updatedPhaseProgress);
                    const currentPhaseIndex = activePhaseIds.indexOf(phaseId);
                    const nextPhaseId = activePhaseIds[currentPhaseIndex + 1];

                    updatedPhaseProgress[phaseId] = {
                        ...updatedPhaseProgress[phaseId],
                        status: 'skipped',
                        completedAt: new Date().toISOString(),
                        notes: t('pages.productionPhase.skippedManually')
                    };

                    if (nextPhaseId) {
                        updatedPhaseProgress[nextPhaseId] = {
                            ...updatedPhaseProgress[nextPhaseId],
                            status: 'in-progress',
                            startedAt: new Date().toISOString(),
                        };
                    }

                    const historyEntry = {
                        phaseId,
                        phaseName: getPhaseName(phaseId),
                        completedAt: new Date().toISOString(),
                        notes: t('pages.productionPhase.skippedManually'),
                        skipped: true,
                    };

                    const batch = writeBatch(db);
                    batch.update(orderRef, {
                        phaseProgress: updatedPhaseProgress,
                        currentPhase: nextPhaseId || phaseId,
                    });
                    batch.update(orderRef, { phaseHistory: arrayUnion(historyEntry) });
                    await batch.commit();

                    await logActivity(
                        'phase_skipped',
                        `production-order/${orderId}/phase/${phaseId}`,
                        `Fase ${getPhaseName(phaseId)} pulada para OP ${orderData.opCode}`
                    );

                    // Send notification for phase skipped
                    sendPhaseNotification(
                        { ...orderData, id: orderId },
                        phaseId,
                        getPhaseName(phaseId),
                        'phase_skipped',
                        { nextPhaseId }
                    );

                    showToast(`${t('pages.productionPhase.phaseSkipped')} ${getPhaseName(phaseId)}.`, 'warning');

                    setSelectedOrder(null);
                    setShowPhaseDetail(false);
                    await fetchData();
                } catch (error) {
                    console.error("Error skipping phase:", error);
                    showToast(t('pages.productionPhase.errors.skipPhaseError'), 'error');
                }
            }
        );
    };

    // ============================================================================
    // CANCEL ORDER WITH STOCK REVERSAL
    // ============================================================================
    const handleCancelOrder = async (orderId) => {
        showConfirm(
            t('pages.productionPhase.confirmCancel'),
            async () => {
                try {
                    const orderRef = doc(db, PRODUCTION_ORDERS_COLLECTION, orderId);
                    const orderSnap = await getDoc(orderRef);
                    if (!orderSnap.exists()) throw new Error("Order not found");

                    const orderData = orderSnap.data();
                    const opCode = orderData.opCode || orderId;

                    if (orderData.status === 'active' && orderData.deductionLog && orderData.deductionLog.length > 0) {
                        const log = orderData.deductionLog || [];
                        const batch = writeBatch(db);

                        for (const entry of log) {
                            const stockDocRef = doc(db, entry.stockCollection, entry.stockDocId);
                            const stockSnap = await getDoc(stockDocRef);
                            const currentQty = stockSnap.exists() ? (parseFloat(stockSnap.data().quantity) || 0) : 0;
                            const newQty = parseFloat((currentQty + entry.quantity).toFixed(4));
                            batch.update(stockDocRef, { quantity: newQty });
                        }

                        batch.delete(orderRef);
                        await batch.commit();

                        const byCollection = log.reduce((acc, entry) => {
                            if (!acc[entry.stockCollection]) acc[entry.stockCollection] = [];
                            acc[entry.stockCollection].push(entry);
                            return acc;
                        }, {});

                        const reversalPromises = [];
                        for (const [colName, entries] of Object.entries(byCollection)) {
                            const entryCollection = STOCK_ENTRY_MAP[colName];
                            if (entryCollection) {
                                reversalPromises.push(
                                    addDoc(collection(db, entryCollection), {
                                        type: 'ENTRADA',
                                        reason: 'cancellation',
                                        notes: `Cancellation of OP ${opCode}`,
                                        createdAt: serverTimestamp(),
                                        items: entries.map(e => ({
                                            rawMaterialId: e.materialId,
                                            rawMaterialName: e.materialName,
                                            quantity: e.quantity,
                                            unit: e.unit || null,
                                            locationId: e.location || null,
                                            locationDescription: e.locationDescription || null,
                                            stockDocId: e.stockDocId,
                                        })),
                                    })
                                );
                            }
                        }
                        await Promise.all(reversalPromises);

                        await logActivity(
                            'order_cancelled',
                            `production-order/${orderId}`,
                            `OP ${opCode} cancelada${orderData.status === 'active' ? ' com reversão de estoque' : ''}`
                        );

                        // Send notification for order cancellation
                        const notificationData = {
                            module: 'ordens_de_producao',
                            type: 'order_cancelled',
                            title: '❌ Ordem de Produção Cancelada',
                            message: `OP ${opCode} foi cancelada${orderData.status === 'active' ? ' com reversão de estoque' : ''}`,
                            timestamp: new Date().toISOString(),
                            route: `/producao/ordens-de-producao`,
                            priority: 'Alta',
                            data: {
                                orderId: orderId,
                                opCode: opCode,
                                hadStockDeduction: orderData.status === 'active' && orderData.deductionLog && orderData.deductionLog.length > 0
                            },
                            orderCode: opCode,
                            status: 'cancelled'
                        };
                        addNotification(notificationData);

                        showToast(t('pages.productionPhase.cancelSuccessRevert'), 'success');
                    } else {
                        await deleteDoc(orderRef);

                        // Send notification for order cancellation (without stock reversal)
                        const notificationData = {
                            module: 'ordens_de_producao',
                            type: 'order_cancelled',
                            title: '❌ Ordem de Produção Cancelada',
                            message: `OP ${opCode} foi cancelada`,
                            timestamp: new Date().toISOString(),
                            route: `/producao/ordens-de-producao`,
                            priority: 'Alta',
                            data: {
                                orderId: orderId,
                                opCode: opCode,
                                hadStockDeduction: false
                            },
                            orderCode: opCode,
                            status: 'cancelled'
                        };
                        addNotification(notificationData);

                        showToast(t('pages.productionPhase.cancelSuccess'), 'info');
                    }

                    await fetchData();
                } catch (error) {
                    console.error("Error cancelling order:", error);
                    showToast(t('pages.productionPhase.errors.cancelOrderError'), 'error');
                }
            }
        );
    };

    // ============================================================================
    // GET STATUS BADGE
    // ============================================================================
    const getStatusBadge = (status) => {
        switch (status) {
            case 'awaiting-mp':
                return {
                    label: <><FiClock size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusAwaitingMP')}</>,
                    style: { backgroundColor: '#f39c12', color: 'white' }
                };
            case 'awaiting-start':
                return {
                    label: <><FiPlay size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusAwaitingStart')}</>,
                    style: { backgroundColor: '#3498db', color: 'white' }
                };
            case 'active':
                return {
                    label: <><FiRefreshCw size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusActive')}</>,
                    style: { backgroundColor: '#27ae60', color: 'white' }
                };
            case 'completed':
                return {
                    label: <><FiCheck size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusCompleted')}</>,
                    style: { backgroundColor: '#2ecc71', color: 'white' }
                };
            default:
                return {
                    label: status || t('pages.productionPhase.statusUnknown'),
                    style: { backgroundColor: '#95a5a6', color: 'white' }
                };
        }
    };

    // ============================================================================
    // RENDER: Phase Detail Modal
    // ============================================================================
    const renderPhaseDetailModal = () => {
        if (!selectedOrder || !currentPhase || !showPhaseDetail) return null;

        const progress = selectedOrder.phaseProgress?.[currentPhase.id] || { status: 'pending' };
        const isCurrentPhase = selectedOrder.currentPhase === currentPhase.id;
        const currentNote = phaseNotes[currentPhase.id] || progress.notes || '';

        const getPhaseDescription = (phaseId) => {
            const descMap = {
                'phase-1': t('pages.productionPhase.phaseWeighingTitle'),
                'phase-2': t('pages.productionPhase.phaseProductionStartTitle'),
                'phase-3': t('pages.productionPhase.phaseProductionEndTitle'),
                'phase-4': t('pages.productionPhase.phaseQualityStartTitle'),
                'phase-5': t('pages.productionPhase.phaseQualityProcessTitle'),
                'phase-6': t('pages.productionPhase.phaseQualityReleaseTitle'),
                'phase-7': t('pages.productionPhase.phasePackagingTitle'),
                'phase-8': t('pages.productionPhase.phaseFillingStartTitle'),
                'phase-9': t('pages.productionPhase.phaseFillingEndTitle'),
                'phase-10': t('pages.productionPhase.phaseLabelingTitle')
            };
            return descMap[phaseId] || currentPhase.description || '';
        };

        return (
            <div className={styles.modalBackdrop}>
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: currentPhase?.id === 'phase-1' ? '1300px' : '700px', width: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h2 style={{ margin: 0 }}><FiFlag size={20} style={{ marginRight: '10px' }} /> {t('pages.productionPhase.phase')} {currentPhase.number}: {getPhaseName(currentPhase.id)}</h2>
                            <p style={{ color: '#666', margin: '4px 0 0' }}>{t('pages.productionPhase.op')}: {selectedOrder.opCode} | {getPhaseDescription(currentPhase.id)}</p>
                        </div>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: `${getPhaseStatusColor(progress.status)}20`, borderLeft: `4px solid ${getPhaseStatusColor(progress.status)}`, borderRadius: '4px', marginBottom: '20px' }}>
                        <strong>{t('pages.productionPhase.phaseStatus')}: </strong>
                        <span style={{ color: getPhaseStatusColor(progress.status), textTransform: 'uppercase' }}>
                            {progress.status === 'in-progress' ? t('pages.productionPhase.phaseInProgress') :
                                progress.status === 'completed' ? t('pages.productionPhase.phaseCompleted') :
                                    progress.status === 'skipped' ? t('pages.productionPhase.phaseSkipped') :
                                        t('pages.productionPhase.phasePending')}
                        </span>
                        {progress.startedAt && <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>{t('pages.productionPhase.phaseStartedAt', { date: formatTimestamp(progress.startedAt) })}</div>}
                        {progress.completedAt && <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('pages.productionPhase.phaseCompletedAt', { date: formatTimestamp(progress.completedAt) })}</div>}

                        <div style={{ marginTop: '12px' }}>
                            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}><FiClipboard size={14} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseNotesLabel')}</label>
                            <textarea
                                value={currentNote}
                                onChange={(e) => handleNoteChange(selectedOrder.id, currentPhase.id, e.target.value)}
                                placeholder={t('pages.productionPhase.phaseNotesPlaceholder')}
                                rows={3}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>
                                {t('pages.productionPhase.phaseNotesSaved')}
                            </div>
                        </div>
                    </div>

                    {/* PHASE 1 - PESAGEM WITH BATCH SELECTION */}
                    {currentPhase.id === 'phase-1' && (() => {
                        const item = selectedOrder.items?.[0] || {};
                        const productCode = item.productCode || '—';
                        const productName = item.productName || '—';
                        const quantidadeKg = item.mode === 'mass'
                            ? parseFloat(item.quantity) || 0
                            : (() => {
                                const prod = products.find(p => p.id === item.productId);
                                const gram = parseFloat(prod?.gramatura) || 0;
                                const gramKg = (prod?.gramaturaUnit === 'g' || prod?.gramaturaUnit === 'ml') ? gram / 1000 : gram;
                                return (parseFloat(item.quantity) || 0) * gramKg;
                            })();

                        const prod = products.find(p => p.id === item.productId);
                        const formulaMaterials = prod?.rawMaterials || [];

                        const deductionMap = {};
                        (selectedOrder.deductionLog || []).forEach(entry => {
                            if (!deductionMap[entry.materialId]) deductionMap[entry.materialId] = [];
                            deductionMap[entry.materialId].push(entry);
                        });

                        const excludedCategoryIds = allCategories
                            .filter(c => ['EMBALAGEM', 'SERIGRAFIA'].includes(c.name?.toUpperCase()))
                            .map(c => c.id);

                        const rows = formulaMaterials
                            .filter(mp => {
                                const mpData = allRawMaterials.find(rm => rm.id === mp.id);
                                if (!mpData) return true;
                                if (!mpData.categoryIds || mpData.categoryIds.length === 0) return true;
                                return !mpData.categoryIds.some(catId => excludedCategoryIds.includes(catId));
                            })
                            .map(mp => {
                                const percentage = parseFloat(mp.quantity) || 0;
                                const quantidadeSugeridaLote = parseFloat(((quantidadeKg * percentage) / 100).toFixed(4));
                                const mpData = allRawMaterials.find(rm => rm.id === mp.id);
                                const rowKey = `${selectedOrder.id}-${mp.id}`;
                                const rowState = pesagemRows[rowKey] || { correcao: '', quantidadeUtilizada: quantidadeSugeridaLote.toFixed(3), obs: '' };
                                const isWater = isWaterMaterial(mpData);

                                const availableBatchesList = batchOptions[mp.id] || [];
                                const hasBatches = availableBatchesList.length > 0;
                                const materialBatchSelection = batchSelections[mp.id];
                                const isLoadingBatches = loadingBatches[mp.id];

                                const requiresBatch = mpData ? requiresBatchTracking(mpData) : false;
                                const canUseWithoutBatch = !requiresBatch && !hasBatches;

                                let statusLabel = '—';
                                let statusColor = '#6c757d';
                                if (isWater) {
                                    statusLabel = '💧 Água';
                                    statusColor = '#1976d2';
                                } else if (rowState.quantidadeUtilizada && parseFloat(rowState.quantidadeUtilizada) > 0) {
                                    if (materialBatchSelection?.batchId) {
                                        statusLabel = '✅ Lote selecionado';
                                        statusColor = '#28a745';
                                    } else if (canUseWithoutBatch) {
                                        statusLabel = '⏳ Aguardando confirmação';
                                        statusColor = '#f39c12';
                                    } else {
                                        statusLabel = '❌ Selecionar lote';
                                        statusColor = '#dc3545';
                                    }
                                }

                                return {
                                    id: mp.id,
                                    rowKey,
                                    inciName: mp.inciName || mpData?.inciName || mpData?.commercialName || mp.id,
                                    percentage,
                                    fase: mp.fase || '—',
                                    quantidadeSugeridaLote,
                                    correcao: rowState.correcao,
                                    quantidadeUtilizada: rowState.quantidadeUtilizada || quantidadeSugeridaLote.toFixed(3),
                                    obs: rowState.obs,
                                    isWater,
                                    batchSelection: materialBatchSelection,
                                    availableBatches: availableBatchesList,
                                    isLoadingBatches,
                                    hasBatches,
                                    requiresBatch,
                                    canUseWithoutBatch,
                                    mpData,
                                    statusLabel,
                                    statusColor
                                };
                            });

                        const updateRow = (rowKey, field, value) => {
                            setPesagemRows(prev => ({
                                ...prev,
                                [rowKey]: { ...(prev[rowKey] || { correcao: '', quantidadeUtilizada: '', obs: '' }), [field]: value }
                            }));

                            if (field === 'quantidadeUtilizada' && value && parseFloat(value) > 0) {
                                const materialId = rowKey.split('-')[1];
                                const material = selectedOrder?.materialsRequired?.find(m => m.id === materialId);
                                if (material && !batchSelections[materialId]) {
                                    const batches = batchOptions[materialId] || [];
                                    if (batches.length > 0) {
                                        const bestBatch = batches.find(b => !b.isExpired) || batches[0];
                                        if (bestBatch && bestBatch.availableQuantity >= parseFloat(value) - 0.0001) {
                                            handleBatchSelect(materialId, bestBatch, parseFloat(value), material.name, 'Kg');
                                        }
                                    }
                                }
                            }
                        };

                        const cellStyle = { padding: '6px 8px', borderBottom: '1px solid #e9ecef', fontSize: '0.78rem', verticalAlign: 'middle', whiteSpace: 'nowrap' };
                        const inputStyle = { width: '80px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.78rem', textAlign: 'right' };
                        const obsInputStyle = { width: '100px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.78rem' };
                        const batchSelectStyle = { width: '180px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.75rem' };

                        return (
                            <div>
                                <div style={{
                                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px',
                                    marginBottom: '20px', padding: '16px', backgroundColor: '#f0f4ff',
                                    borderRadius: '8px', border: '1px solid #c7d2fe',
                                }}>
                                    {[
                                        { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode },
                                        { label: t('pages.productionPhase.productCode'), value: productCode },
                                        { label: t('pages.productionPhase.productName'), value: productName },
                                        { label: t('pages.productionPhase.quantityKg'), value: `${quantidadeKg.toFixed(3)} Kg` },
                                    ].map(({ label, value }) => (
                                        <div key={label}>
                                            <div style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e1b4b' }}>{value}</div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '10px', color: '#374151' }}><FiPackage size={16} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingSubtitle')}</h3>
                                    <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1400px' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#1e1b4b', color: 'white' }}>
                                                    {[
                                                        t('pages.productionPhase.phaseWeighingColumns.inciName'),
                                                        t('pages.productionPhase.phaseWeighingColumns.percentage'),
                                                        t('pages.productionPhase.phaseWeighingColumns.phase'),
                                                        t('pages.productionPhase.phaseWeighingColumns.suggestedQty'),
                                                        t('pages.productionPhase.phaseWeighingColumns.usedQty'),
                                                        t('pages.productionPhase.phaseWeighingColumns.batch'),
                                                        t('pages.productionPhase.phaseWeighingColumns.location'),
                                                        t('pages.productionPhase.phaseWeighingColumns.expiration'),
                                                        t('pages.productionPhase.phaseWeighingColumns.obs'),
                                                        t('pages.productionPhase.phaseWeighingColumns.correction'),
                                                        'Status'
                                                    ].map(col => (
                                                        <th key={col} style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: col === t('pages.productionPhase.phaseWeighingColumns.inciName') ? 'left' : 'center', whiteSpace: 'nowrap', letterSpacing: '0.03em' }}>{col}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.length === 0 && (
                                                    <tr><td colSpan={11} style={{ ...cellStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }}>{t('pages.productionPhase.phaseWeighingNoMaterials')}</td></tr>
                                                )}
                                                {rows.map((row, idx) => {
                                                    const usedQty = parseFloat(row.quantidadeUtilizada) || row.quantidadeSugeridaLote;
                                                    const isSelectionValid = row.isWater ||
                                                        (row.canUseWithoutBatch && row.quantidadeUtilizada) ||
                                                        (row.batchSelection && row.batchSelection.selectedQuantity >= usedQty - 0.0001);

                                                    return (
                                                        <tr key={row.rowKey} style={{
                                                            backgroundColor: row.isWater ? '#e3f2fd' : (idx % 2 === 0 ? '#ffffff' : '#f9fafb'),
                                                            borderLeft: !isSelectionValid && row.quantidadeUtilizada && !row.isWater ? '3px solid #dc3545' : 'none'
                                                        }}>
                                                            <td style={{ ...cellStyle, whiteSpace: 'normal', minWidth: '140px', fontWeight: 500 }}>
                                                                {row.inciName}
                                                                {row.isWater && (
                                                                    <span style={{
                                                                        marginLeft: '8px',
                                                                        fontSize: '0.7rem',
                                                                        padding: '2px 6px',
                                                                        backgroundColor: '#e3f2fd',
                                                                        borderRadius: '12px',
                                                                        color: '#1976d2'
                                                                    }}>
                                                                        <FiDroplet size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingWater')}
                                                                    </span>
                                                                )}
                                                                {!row.isWater && !row.hasBatches && !row.isLoadingBatches && (
                                                                    <span style={{
                                                                        marginLeft: '8px',
                                                                        fontSize: '0.7rem',
                                                                        padding: '2px 6px',
                                                                        backgroundColor: '#fff3cd',
                                                                        borderRadius: '12px',
                                                                        color: '#856404'
                                                                    }}>
                                                                        <FiInfo size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingNoBatch')}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.percentage.toFixed(2)}%</td>
                                                            <td style={{ ...cellStyle, textAlign: 'center', color: '#6b7280' }}>{row.fase}</td>
                                                            <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 600, color: '#1e1b4b' }}>{row.quantidadeSugeridaLote.toFixed(3)}</td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                <input
                                                                    type="number"
                                                                    step="0.001"
                                                                    placeholder="0.000"
                                                                    value={row.quantidadeUtilizada || row.quantidadeSugeridaLote.toFixed(3)}
                                                                    onChange={e => {
                                                                        const newValue = e.target.value;
                                                                        updateRow(row.rowKey, 'quantidadeUtilizada', newValue);
                                                                        if (row.batchSelection && Math.abs(parseFloat(newValue) - (row.batchSelection.selectedQuantity || 0)) > 0.001) {
                                                                            setBatchSelections(prev => {
                                                                                const newSelections = { ...prev };
                                                                                delete newSelections[row.id];
                                                                                return newSelections;
                                                                            });
                                                                        }
                                                                        if (newValue && parseFloat(newValue) > 0 && row.availableBatches.length > 0 && !row.batchSelection) {
                                                                            const bestBatch = row.availableBatches.find(b => !b.isExpired) || row.availableBatches[0];
                                                                            if (bestBatch) {
                                                                                const usedQtyBatch = parseFloat(newValue) || 0;
                                                                                handleBatchSelect(row.id, bestBatch, usedQtyBatch, row.inciName, 'Kg');
                                                                            }
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        ...inputStyle,
                                                                        borderColor: row.quantidadeUtilizada && (!isSelectionValid && !row.isWater) ? '#dc3545' : '#ced4da',
                                                                        backgroundColor: row.quantidadeUtilizada ? '#f0f9ff' : 'white'
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                {row.isWater ? (
                                                                    <span style={{ color: '#1976d2', fontSize: '0.7rem' }}>{t('pages.productionPhase.phaseWeighingInfinite')}</span>
                                                                ) : row.isLoadingBatches ? (
                                                                    <span style={{ fontSize: '0.7rem', color: '#666' }}><FiRefreshCw size={12} className={styles.buttonSpinner} style={{ marginRight: '4px' }} /> {t('common.loading')}</span>
                                                                ) : row.requiresBatch ? (
                                                                    row.hasBatches ? (
                                                                        <>
                                                                            <select
                                                                                value={row.batchSelection?.batchId || ''}
                                                                                onChange={(e) => {
                                                                                    const batch = row.availableBatches.find(b => b.stockId === e.target.value);
                                                                                    if (batch) {
                                                                                        handleBatchSelect(row.id, batch, usedQty, row.inciName, 'Kg');
                                                                                    }
                                                                                }}
                                                                                style={batchSelectStyle}
                                                                                disabled={!row.quantidadeUtilizada && !row.correcao}
                                                                            >
                                                                                <option value="">{t('pages.productionPhase.phaseWeighingSelectBatch')}</option>
                                                                                {row.availableBatches.map(batch => {
                                                                                    const isExpiredBatch = batch.isExpired;
                                                                                    return (
                                                                                        <option
                                                                                            key={batch.stockId}
                                                                                            value={batch.stockId}
                                                                                            style={{ color: isExpiredBatch ? '#dc3545' : 'inherit' }}
                                                                                        >
                                                                                            {batch.lotNumber || t('pages.productionPhase.phaseWeighingNoBatch')}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </>
                                                                    ) : (
                                                                        <span style={{ color: '#dc3545', fontSize: '0.7rem' }}>
                                                                            <FiXCircle size={12} style={{ marginRight: '4px' }} />
                                                                            {t('pages.productionPhase.phaseWeighingNoStock')}
                                                                            <div style={{ fontSize: '0.65rem', color: '#dc3545', marginTop: '2px' }}>
                                                                                <FiAlertCircle size={12} style={{ marginRight: '4px' }} />
                                                                                {t('pages.productionPhase.phaseWeighingBatchRequired')}
                                                                            </div>
                                                                        </span>
                                                                    )
                                                                ) : (
                                                                    row.hasBatches ? (
                                                                        <>
                                                                            <select
                                                                                value={row.batchSelection?.batchId || ''}
                                                                                onChange={(e) => {
                                                                                    const batch = row.availableBatches.find(b => b.stockId === e.target.value);
                                                                                    if (batch) {
                                                                                        handleBatchSelect(row.id, batch, usedQty, row.inciName, 'Kg');
                                                                                    }
                                                                                }}
                                                                                style={batchSelectStyle}
                                                                                disabled={!row.quantidadeUtilizada && !row.correcao}
                                                                            >
                                                                                <option value="">{t('pages.productionPhase.phaseWeighingSelectBatch')}</option>
                                                                                {row.availableBatches.map(batch => {
                                                                                    const isExpiredBatch = batch.isExpired;
                                                                                    return (
                                                                                        <option
                                                                                            key={batch.stockId}
                                                                                            value={batch.stockId}
                                                                                            style={{ color: isExpiredBatch ? '#dc3545' : 'inherit' }}
                                                                                        >
                                                                                            {batch.lotNumber || t('pages.productionPhase.phaseWeighingNoBatch')}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </>
                                                                    ) : row.canUseWithoutBatch ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>{t('pages.productionPhase.phaseWeighingNoBatch')}</span>
                                                                            {row.quantidadeUtilizada && !row.batchSelection && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleNoBatchMaterial(row.id, usedQty, row.inciName, 'Kg')}
                                                                                    style={{
                                                                                        padding: '2px 8px',
                                                                                        fontSize: '0.7rem',
                                                                                        backgroundColor: '#28a745',
                                                                                        color: 'white',
                                                                                        border: 'none',
                                                                                        borderRadius: '4px',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                >
                                                                                    <FiCheck size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingConfirmUse')}
                                                                                </button>
                                                                            )}
                                                                            {row.batchSelection && !row.batchSelection.hasBatch && (
                                                                                <span style={{ fontSize: '0.65rem', color: '#28a745' }}><FiCheck size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingMaterialConfirmed')}</span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ color: '#dc3545', fontSize: '0.7rem' }}>
                                                                            <FiXCircle size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingNoStock')}
                                                                        </span>
                                                                    )
                                                                )}
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                {!row.isWater && row.batchSelection?.locationId ? row.batchSelection.locationId : '—'}
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                {!row.isWater && row.batchSelection?.expirationDate ? formatDate(row.batchSelection.expirationDate) : '—'}
                                                                {!row.isWater && row.batchSelection?.expirationDate && isExpired(row.batchSelection.expirationDate) && (
                                                                    <span style={{ marginLeft: '4px', fontSize: '10px', color: '#dc3545' }}>{t('pages.productionPhase.phaseWeighingExpired')}</span>
                                                                )}
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                <input type="text" placeholder={t('pages.productionPhase.phaseWeighingColumns.obs')} value={row.obs} onChange={e => updateRow(row.rowKey, 'obs', e.target.value)} style={obsInputStyle} />
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                <input type="number" step="0.001" placeholder="0.000" value={row.correcao} onChange={e => updateRow(row.rowKey, 'correcao', e.target.value)} style={inputStyle} />
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                                <span style={{ color: row.statusColor, fontSize: '0.7rem', fontWeight: 500 }}>
                                                                    {row.statusLabel}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {floatMaterials.length > 0 && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#b45309', marginBottom: '6px' }}><FiPlus size={14} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseWeighingAddMaterial')}</h4>
                                        <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid #fde68a' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#fef3c7' }}>
                                                        {[
                                                            t('pages.productionPhase.phaseWeighingAddModalInci'),
                                                            t('pages.productionPhase.phaseWeighingAddModalQty'),
                                                            t('pages.productionPhase.phaseWeighingAddModalObs'),
                                                            ''
                                                        ].map(h => (
                                                            <th key={h} style={{ padding: '6px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'left' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {floatMaterials.map((fm, idx) => (
                                                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fffbeb' : '#fff' }}>
                                                            <td style={{ ...cellStyle }}>{fm.inciName}</td>
                                                            <td style={{ ...cellStyle }}>{fm.quantidade} Kg</td>
                                                            <td style={{ ...cellStyle }}>{fm.obs || '—'}</td>
                                                            <td style={{ ...cellStyle }}>
                                                                <button onClick={() => setFloatMaterials(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem' }} title={t('common.remove')}><FiX size={14} /></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <div style={{ marginBottom: '16px' }}>
                                    <button onClick={() => setShowFloatModal(true)} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'underline', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                        <FiPlus size={14} /> {t('pages.productionPhase.phaseWeighingAddMaterial')}
                                    </button>
                                </div>
                            </div>
                        );
                    })()}

                    {/* PHASE 2 - Inicio da Producao */}
                    {currentPhase.id === 'phase-2' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#f0f4ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.currentDate'), value: currentDate },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e1b4b' }}>{value}</div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '16px', color: '#374151' }}>
                                    <FiCalendar size={16} style={{ marginRight: '4px' }} />
                                    {t('pages.productionPhase.phaseProductionStartTitle')}
                                </h3>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseProductionStartFabrication')} <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={fabricationDate}
                                            onChange={(e) => {
                                                const newDate = e.target.value;
                                                setFabricationDate(newDate);

                                                if (newDate) {
                                                    // Buscar o produto para obter os meses de validade
                                                    const product = products.find(p => p.id === selectedOrder?.items?.[0]?.productId);
                                                    const validityMonths = product?.shelfLifeMonths || product?.technical?.shelfLifeMonths || 24;

                                                    // Calcular data de validade
                                                    const date = new Date(newDate);
                                                    date.setMonth(date.getMonth() + parseInt(validityMonths));

                                                    // Formatar para YYYY-MM-DD (formato do input date)
                                                    const year = date.getFullYear();
                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                    const day = String(date.getDate()).padStart(2, '0');
                                                    const expirationDateFormatted = `${year}-${month}-${day}`;

                                                    setExpirationDate(expirationDateFormatted);
                                                } else {
                                                    setExpirationDate('');
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '0.9rem',
                                                boxSizing: 'border-box',
                                                fontFamily: 'inherit',
                                                backgroundColor: '#ffffff'
                                            }}
                                        />
                                        <small style={{ display: 'block', marginTop: '4px', fontSize: '0.7rem', color: '#6b7280' }}>
                                            {t('pages.productionPhase.phaseProductionStartFabricationHint', {
                                                defaultMessage: 'Selecione a data de fabricação no calendário'
                                            })}
                                        </small>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseProductionStartExpiration')}
                                        </label>
                                        <div style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '0.9rem',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#f9fafb',
                                            color: expirationDate ? '#059669' : '#9ca3af',
                                            fontWeight: expirationDate ? 600 : 400,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span>
                                                {expirationDate ? new Date(expirationDate).toLocaleDateString('pt-BR') : t('pages.productionPhase.phaseProductionStartExpirationPlaceholder')}
                                            </span>
                                            {expirationDate && (
                                                <span style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 'normal' }}>
                                                    {t('pages.productionPhase.phaseProductionStartAutoCalculated')}
                                                </span>
                                            )}
                                        </div>
                                        {expirationDate && (
                                            <small style={{ display: 'block', marginTop: '4px', fontSize: '0.7rem', color: '#6b7280' }}>
                                                {t('pages.productionPhase.phaseProductionStartExpirationInfo', {
                                                    months: products.find(p => p.id === selectedOrder?.items?.[0]?.productId)?.shelfLifeMonths ||
                                                        products.find(p => p.id === selectedOrder?.items?.[0]?.productId)?.technical?.shelfLifeMonths ||
                                                        24
                                                })}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PHASE 3 - Termino da Producao */}
                    {currentPhase.id === 'phase-3' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.completionDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#064e3b' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px', textAlign: 'center' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🏁</div>
                                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#059669', marginBottom: '8px' }}>{t('pages.productionPhase.phaseProductionEndTitle')}</h3>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0' }}>
                                    {t('pages.productionPhase.phaseProductionEndMessage')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* PHASE 4 - Inicio do CQ */}
                    {currentPhase.id === 'phase-4' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#e8f4fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.qualityStartDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#1976d2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0d47a1' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px', textAlign: 'center' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔬</div>
                                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1976d2', marginBottom: '8px' }}>{t('pages.productionPhase.phaseQualityStartTitle')}</h3>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0' }}>
                                    {t('pages.productionPhase.phaseQualityStartMessage')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* PHASE 5 - CQ em Processo */}
                    {currentPhase.id === 'phase-5' && (() => {
                        const product = products.find(p => p.id === selectedOrder?.items?.[0]?.productId);
                        const criteriaFromProduct = product?.qualityCriteria || [];

                        // Use qualityResults diretamente do estado
                        const results = qualityResults;

                        // Função para atualizar um critério
                        const updateCriterionResult = (criterionId, field, value) => {
                            setQualityResults(prev => ({
                                ...prev,
                                [criterionId]: { ...prev[criterionId], [field]: value }
                            }));
                        };

                        // Conta status para resumo
                        const statusCounts = Object.values(results).reduce((acc, r) => {
                            const status = r?.status || 'pending';
                            acc[status] = (acc[status] || 0) + 1;
                            return acc;
                        }, { pending: 0, approved: 0, restricted: 0, rejected: 0 });

                        const totalCriteria = criteriaFromProduct.length;
                        const hasAllApproved = totalCriteria > 0 &&
                            Object.values(results).every(r => r?.status === 'approved');
                        const hasAnyRejected = Object.values(results).some(r => r?.status === 'rejected');
                        const hasAnyRestricted = Object.values(results).some(r => r?.status === 'restricted');

                        // Helper para badge de status
                        const getStatusBadge = (status) => {
                            const badges = {
                                'pending': { label: '⏳ Pendente', color: '#6c757d', bg: '#f8f9fa' },
                                'approved': { label: '✅ Aprovado', color: '#28a745', bg: '#d4edda' },
                                'restricted': { label: '⚠️ Com restrições', color: '#fd7e14', bg: '#fff3cd' },
                                'rejected': { label: '❌ Rejeitado', color: '#dc3545', bg: '#f8d7da' },
                            };
                            return badges[status] || badges['pending'];
                        };

                        // Se não houver critérios, mostra mensagem
                        if (criteriaFromProduct.length === 0) {
                            return (
                                <div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#fff8e1', borderRadius: '8px', border: '1px solid #ffe082' }}>
                                        {[
                                            { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                            { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                            { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                            { label: t('pages.productionPhase.dateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                        ].map(({ label, value }) => (
                                            <div key={label}>
                                                <div style={{ fontSize: '0.72rem', color: '#f57c00', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#e65100' }}>{value}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📋</div>
                                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#f57c00', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseQualityProcessTitle')}
                                        </h3>
                                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0' }}>
                                            {t('pages.productionPhase.phaseQualityProcessNoCriteria')}
                                        </p>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#fff8e1', borderRadius: '8px', border: '1px solid #ffe082' }}>
                                    {[
                                        { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                        { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                        { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                        { label: t('pages.productionPhase.dateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                    ].map(({ label, value }) => (
                                        <div key={label}>
                                            <div style={{ fontSize: '0.72rem', color: '#f57c00', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#e65100' }}>{value}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Criteria Table */}
                                <div style={{ marginBottom: '20px' }}>
                                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '10px', color: '#374151' }}>
                                        <FiCheckCircle size={16} style={{ marginRight: '4px' }} />
                                        {t('pages.productionPhase.phaseQualityProcessTitle')}
                                    </h3>
                                    <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#1e1b4b', color: 'white' }}>
                                                    <th style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>
                                                        {t('pages.products.form.analysis')}
                                                    </th>
                                                    <th style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>
                                                        {t('pages.products.form.method')}
                                                    </th>
                                                    <th style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>
                                                        {t('pages.products.form.specification')}
                                                    </th>
                                                    <th style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        {t('pages.productionPhase.phaseQualityResult')}
                                                    </th>
                                                    <th style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        {t('pages.productionPhase.phaseQualityObservation')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {criteriaFromProduct.map((criterion, index) => {
                                                    const result = results[criterion.id] || { status: 'pending', observation: '' };
                                                    const statusBadge = getStatusBadge(result.status);

                                                    return (
                                                        <tr key={criterion.id} style={{
                                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                            borderLeft: result.status === 'rejected' ? '3px solid #dc3545' :
                                                                result.status === 'restricted' ? '3px solid #fd7e14' :
                                                                    result.status === 'approved' ? '3px solid #28a745' : 'none'
                                                        }}>
                                                            <td style={{ padding: '8px 10px', fontSize: '0.82rem', verticalAlign: 'middle' }}>
                                                                {criterion.analysis || '—'}
                                                            </td>
                                                            <td style={{ padding: '8px 10px', fontSize: '0.82rem', verticalAlign: 'middle' }}>
                                                                {criterion.method || '—'}
                                                            </td>
                                                            <td style={{ padding: '8px 10px', fontSize: '0.82rem', verticalAlign: 'middle' }}>
                                                                {criterion.specification || '—'}
                                                            </td>
                                                            <td style={{ padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                                <select
                                                                    value={result.status || 'pending'}
                                                                    onChange={(e) => updateCriterionResult(criterion.id, 'status', e.target.value)}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        border: `1px solid ${statusBadge.color}`,
                                                                        backgroundColor: statusBadge.bg,
                                                                        color: statusBadge.color,
                                                                        fontSize: '0.78rem',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        minWidth: '120px'
                                                                    }}
                                                                    disabled={!isCurrentPhase}
                                                                >
                                                                    <option value="pending" style={{ color: '#6c757d' }}>⏳ Pendente</option>
                                                                    <option value="approved" style={{ color: '#28a745' }}>✅ Aprovado</option>
                                                                    <option value="restricted" style={{ color: '#fd7e14' }}>⚠️ Com restrições</option>
                                                                    <option value="rejected" style={{ color: '#dc3545' }}>❌ Rejeitado</option>
                                                                </select>
                                                            </td>
                                                            <td style={{ padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                                <input
                                                                    type="text"
                                                                    value={result.observation || ''}
                                                                    onChange={(e) => updateCriterionResult(criterion.id, 'observation', e.target.value)}
                                                                    placeholder={t('pages.productionPhase.phaseQualityObservationPlaceholder')}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        border: '1px solid #d1d5db',
                                                                        fontSize: '0.78rem',
                                                                        width: '100%',
                                                                        minWidth: '120px',
                                                                        maxWidth: '200px',
                                                                        boxSizing: 'border-box'
                                                                    }}
                                                                    disabled={!isCurrentPhase}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Status Messages */}
                                {hasAnyRejected && (
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#f8d7da',
                                        borderRadius: '8px',
                                        border: '1px solid #f5c6cb',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <FiXCircle size={18} color="#dc3545" />
                                        <span style={{ color: '#721c24', fontSize: '0.85rem' }}>
                                            {t('pages.productionPhase.phaseQualityHasRejected')}
                                        </span>
                                    </div>
                                )}

                                {hasAnyRestricted && !hasAnyRejected && (
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#fff3cd',
                                        borderRadius: '8px',
                                        border: '1px solid #ffeeba',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <FiAlertCircle size={18} color="#fd7e14" />
                                        <span style={{ color: '#856404', fontSize: '0.85rem' }}>
                                            {t('pages.productionPhase.phaseQualityHasRestricted')}
                                        </span>
                                    </div>
                                )}

                                {hasAllApproved && totalCriteria > 0 && (
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#d4edda',
                                        borderRadius: '8px',
                                        border: '1px solid #c3e6cb',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <FiCheckCircle size={18} color="#28a745" />
                                        <span style={{ color: '#155724', fontSize: '0.85rem' }}>
                                            {t('pages.productionPhase.phaseQualityAllApproved')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* PHASE 6 - Liberacao do CQ */}
                    {currentPhase.id === 'phase-6' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.releaseDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1b5e20' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '16px', color: '#374151' }}><FiCheckCircle size={16} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseQualityReleaseTitle')}</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{t('pages.productionPhase.phaseQualityReleasePH')}</label>
                                        <input type="number" step="0.01" value={phValue} onChange={(e) => setPhValue(e.target.value)} placeholder="Ex: 5.5" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{t('pages.productionPhase.phaseQualityReleaseAppearance')}</label>
                                        <input type="text" value={appearance} onChange={(e) => setAppearance(e.target.value)} placeholder="Ex: Liquido transparente, Gel homogeneo..." style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}><FiClipboard size={14} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseQualityReleaseObservations')}</label>
                                    <textarea value={qualityObservations} onChange={(e) => setQualityObservations(e.target.value)} placeholder={t('pages.productionPhase.phaseQualityReleasePlaceholder')} rows={4} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PHASE 7 - Conferencia de Embalagem */}
                    {currentPhase.id === 'phase-7' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.inspectionDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#92400e' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px', textAlign: 'center' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📦</div>
                                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#d97706', marginBottom: '8px' }}>{t('pages.productionPhase.phasePackagingTitle')}</h3>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '16px' }}>
                                    {t('pages.productionPhase.phasePackagingMessage')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* PHASE 8 - Inicio do Envase */}
                    {currentPhase.id === 'phase-8' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#e0f2fe', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.fillingStartDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#0284c7', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0369a1' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '16px', color: '#374151' }}><FiSettings size={16} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseFillingStartTitle')}</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{t('pages.productionPhase.phaseFillingStartFunil')}</label>
                                        <select value={funilSelecionado} onChange={(e) => setFunilSelecionado(e.target.value)} className={styles.input} style={{ width: '100%' }} required>
                                            <option value="">{t('pages.productionPhase.phaseFillingStartFunilPlaceholder')}</option>
                                            {funilOptions.map(proc => (<option key={proc.id} value={proc.id}>{proc.name}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{t('pages.productionPhase.phaseFillingStartMangueira')}</label>
                                        <select value={mangueiraSelecionada} onChange={(e) => setMangueiraSelecionada(e.target.value)} className={styles.input} style={{ width: '100%' }} disabled={mangueiraOptions.length === 0}>
                                            <option value="">{t('pages.productionPhase.phaseFillingStartMangueiraPlaceholder')}</option>
                                            {mangueiraOptions.map(proc => (<option key={proc.id} value={proc.id}>{proc.name}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{t('pages.productionPhase.phaseFillingStartMaquina')}</label>
                                        <select value={maquinaEnvaseSelecionada} onChange={(e) => setMaquinaEnvaseSelecionada(e.target.value)} className={styles.input} style={{ width: '100%' }} required>
                                            <option value="">{t('pages.productionPhase.phaseFillingStartMaquinaPlaceholder')}</option>
                                            {maquinaEnvaseOptions.map(proc => (<option key={proc.id} value={proc.id}>{proc.name}</option>))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PHASE 9 - Termino do Envase */}
                    {currentPhase.id === 'phase-9' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.fillingEndDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#14532d' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}><FiClipboard size={14} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.phaseFillingEndTitle')}</label>
                                    <textarea
                                        value={envaseObservations}
                                        onChange={(e) => setEnvaseObservations(e.target.value)}
                                        placeholder={t('pages.productionPhase.phaseFillingEndPlaceholder')}
                                        rows={6}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PHASE 10 - Termino da Rotulagem */}
                    {currentPhase.id === 'phase-10' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: '#f3e8ff', borderRadius: '8px', border: '1px solid #d8b4fe' }}>
                                {[
                                    { label: t('pages.productionPhase.opCode'), value: selectedOrder.opCode || '—' },
                                    { label: t('pages.productionPhase.productCode'), value: selectedOrder.items?.[0]?.productCode || '—' },
                                    { label: t('pages.productionPhase.productName'), value: selectedOrder.items?.[0]?.productName || '—' },
                                    { label: t('pages.productionPhase.labelingEndDateTime'), value: `${currentDate} ${new Date().toLocaleTimeString('pt-BR')}` },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.72rem', color: '#7e22ce', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#4c1d95' }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '8px' }}>🏷️</div>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#7e22ce', marginBottom: '4px' }}>{t('pages.productionPhase.phaseLabelingTitle')}</h3>
                                    <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>{t('pages.productionPhase.phaseLabelingSubtitle')}</p>
                                </div>

                                {/* NEW: Location Selection for Finished Product - Using registered locations */}
                                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f3ff', borderRadius: '8px', border: '1px solid #ddd6fe' }}>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                        <FiMapPin size={14} style={{ marginRight: '4px' }} />
                                        {t('pages.productionPhase.phaseLabelingDestinationLocation', 'Localização de Destino do Produto Acabado')}
                                        <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <select
                                        value={rotulagemData.location || ''}
                                        onChange={(e) => setRotulagemData(prev => ({ ...prev, location: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '0.9rem',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#ffffff'
                                        }}
                                        required
                                    >
                                        <option value="">{t('pages.productionPhase.phaseLabelingSelectLocation', 'Selecione a localização...')}</option>
                                        {allLocations
                                            .filter(loc => loc.category === 'embalagem_acabado') // Filter for finished product locations
                                            .sort((a, b) => a.id.localeCompare(b.id))
                                            .map(location => (
                                                <option key={location.id} value={location.id}>
                                                    {location.id} {location.description ? `- ${location.description}` : ''}
                                                    {location.categoryName && ` (${location.categoryName})`}
                                                </option>
                                            ))}
                                    </select>
                                    <small style={{ display: 'block', marginTop: '4px', fontSize: '0.7rem', color: '#6b7280' }}>
                                        {t('pages.productionPhase.phaseLabelingLocationHint', 'Selecione onde o produto acabado será armazenado')}
                                    </small>
                                    {allLocations.filter(loc => loc.category === 'embalagem_acabado').length === 0 && (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '8px 12px',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '4px',
                                            border: '1px solid #f59e0b',
                                            fontSize: '0.8rem',
                                            color: '#92400e'
                                        }}>
                                            <FiAlertCircle size={14} style={{ marginRight: '8px' }} />
                                            {t('pages.productionPhase.phaseLabelingNoLocations', 'Nenhuma localização cadastrada para produtos acabados. Cadastre uma localização com a categoria "Embalagem e Produto Acabado" em Cadastro de Localizações.')}
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                        <FiClipboard size={14} style={{ marginRight: '4px' }} />
                                        {t('pages.productionPhase.phaseLabelingObservations')}
                                    </label>
                                    <textarea
                                        value={rotulagemData.observacoesQualidade}
                                        onChange={(e) => setRotulagemData(prev => ({ ...prev, observacoesQualidade: e.target.value }))}
                                        placeholder={t('pages.productionPhase.phaseLabelingObservationsPlaceholder')}
                                        rows={4}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseLabelingEmbalada')}
                                        </label>
                                        <input
                                            type="text"
                                            value={rotulagemData.quantidadeEmbalada}
                                            onChange={(e) => setRotulagemData(prev => ({ ...prev, quantidadeEmbalada: e.target.value.replace(',', '.') }))}
                                            placeholder={t('pages.productionPhase.phaseLabelingEmbaladaPlaceholder')}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseLabelingCaixas')}
                                        </label>
                                        <input
                                            type="text"
                                            value={rotulagemData.quantidadeCaixas}
                                            onChange={(e) => setRotulagemData(prev => ({ ...prev, quantidadeCaixas: e.target.value.replace(',', '.') }))}
                                            placeholder={t('pages.productionPhase.phaseLabelingCaixasPlaceholder')}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseLabelingItens')}
                                        </label>
                                        <input
                                            type="text"
                                            value={rotulagemData.itensPorCaixa}
                                            onChange={(e) => setRotulagemData(prev => ({ ...prev, itensPorCaixa: e.target.value.replace(',', '.') }))}
                                            placeholder={t('pages.productionPhase.phaseLabelingItensPlaceholder')}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseLabelingConferenteSelo')}
                                        </label>
                                        <input
                                            type="text"
                                            value={rotulagemData.conferenteSelo}
                                            onChange={(e) => setRotulagemData(prev => ({ ...prev, conferenteSelo: e.target.value }))}
                                            placeholder={t('pages.productionPhase.phaseLabelingConferenteSelo')}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseLabelingConferenteLote')}
                                        </label>
                                        <input
                                            type="text"
                                            value={rotulagemData.conferenteLote}
                                            onChange={(e) => setRotulagemData(prev => ({ ...prev, conferenteLote: e.target.value }))}
                                            placeholder={t('pages.productionPhase.phaseLabelingConferenteLote')}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                            {t('pages.productionPhase.phaseLabelingConferenteRetencao')}
                                        </label>
                                        <input
                                            type="text"
                                            value={rotulagemData.conferenteRetencao}
                                            onChange={(e) => setRotulagemData(prev => ({ ...prev, conferenteRetencao: e.target.value }))}
                                            placeholder={t('pages.productionPhase.phaseLabelingConferenteRetencao')}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {isCurrentPhase && (
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button onClick={() => { setShowPhaseDetail(false); setCurrentPhase(null); }} className={styles.cancelButton}>
                                {t('pages.productionPhase.phaseActionsClose')}
                            </button>
                            {currentPhase.id === 'phase-1' ? (
                                <button
                                    onClick={handleCompletePhase1WithBatches}
                                    style={{ backgroundColor: '#27ae60', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}
                                >
                                    <FiCheck size={18} /> {t('pages.productionPhase.phaseActionsCompleteWeighing')}
                                </button>
                            ) : (
                                <button onClick={() => handleCompletePhase(selectedOrder.id, currentPhase.id)} style={{ backgroundColor: '#27ae60', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}>
                                    <FiCheck size={18} /> {t('pages.productionPhase.phaseActionsComplete')}
                                </button>
                            )}
                        </div>
                    )}

                    {!isCurrentPhase && progress.status !== 'completed' && progress.status !== 'skipped' && (
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button onClick={() => { setShowPhaseDetail(false); setCurrentPhase(null); }} className={styles.cancelButton}>
                                {t('pages.productionPhase.phaseActionsClose')}
                            </button>
                            <button onClick={() => handleSkipPhase(selectedOrder.id, currentPhase.id)} style={{ backgroundColor: '#f39c12', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}>
                                <FiSkipForward size={18} /> {t('pages.productionPhase.phaseActionsSkip')}
                            </button>
                        </div>
                    )}

                    {!isCurrentPhase && (progress.status === 'completed' || progress.status === 'skipped') && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button onClick={() => { setShowPhaseDetail(false); setCurrentPhase(null); }} className={styles.cancelButton}>
                                {t('pages.productionPhase.phaseActionsClose')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ============================================================================
    // RENDER: Phase Timeline
    // ============================================================================
    const renderPhaseTimeline = (order) => {
        if (!order.phaseProgress) return null;

        const activePhaseIds = order.activePhases || Object.keys(order.phaseProgress);
        const activePhasesList = ALL_SYSTEM_PHASES.filter(phase => activePhaseIds.includes(phase.id));
        activePhasesList.sort((a, b) => a.number - b.number);

        const currentPhaseId = order.currentPhase;

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
                {activePhasesList.map((phase) => {
                    const progress = order.phaseProgress[phase.id] || { status: 'pending' };
                    const isCurrent = currentPhaseId === phase.id;
                    const isCompleted = progress.status === 'completed';
                    const isSkipped = progress.status === 'skipped';

                    const displayName = PHASE_DISPLAY_NAMES[phase.id] || phase.name;

                    return (
                        <div
                            key={phase.id}
                            onClick={() => {
                                if (isCurrent || isCompleted || isSkipped) {
                                    setSelectedOrder(order);
                                    setCurrentPhase(phase);
                                    setShowPhaseDetail(true);
                                }
                            }}
                            style={{
                                flex: '1 1 100px',
                                minWidth: '100px',
                                padding: '12px 8px',
                                backgroundColor: isCurrent ? '#3498db' : isCompleted ? '#27ae60' : isSkipped ? '#f39c12' : 'white',
                                color: (isCurrent || isCompleted || isSkipped) ? 'white' : '#333',
                                borderRadius: '8px',
                                border: isCurrent ? '2px solid #2980b9' : '1px solid #ddd',
                                textAlign: 'center',
                                cursor: (isCurrent || isCompleted || isSkipped) ? 'pointer' : 'default',
                                opacity: progress.status === 'pending' && !isCurrent ? 0.5 : 1,
                                transition: 'all 0.3s ease',
                                position: 'relative',
                            }}
                        >
                            <div style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>{t('pages.productionPhase.phase')} {phase.number}</div>
                            <div style={{ fontSize: '0.6rem', marginTop: '2px' }}>
                                {displayName.length > 25 ? displayName.substring(0, 22) + '...' : displayName}
                            </div>
                            {isCurrent && (
                                <div style={{ position: 'absolute', bottom: '-8px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                                    <FiFlag size={10} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.current')}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ============================================================================
    // RENDER: Floating Material Modal
    // ============================================================================
    const renderFloatMaterialModal = () => {
        if (!showFloatModal) return null;

        return (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '420px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700, color: '#1e1b4b' }}><FiPlus size={20} style={{ marginRight: '8px' }} /> {t('pages.productionPhase.phaseWeighingAddModalTitle')}</h3>
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '16px' }}>{t('pages.productionPhase.phaseWeighingAddModalSubtitle')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>{t('pages.productionPhase.phaseWeighingAddModalInci')}</label>
                            <input
                                type="text"
                                placeholder="Nome INCI da materia-prima"
                                value={floatForm.inciName}
                                onChange={e => setFloatForm(prev => ({ ...prev, inciName: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>{t('pages.productionPhase.phaseWeighingAddModalQty')}</label>
                            <input
                                type="number"
                                step="0.001"
                                placeholder="0.000"
                                value={floatForm.quantidade}
                                onChange={e => setFloatForm(prev => ({ ...prev, quantidade: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>{t('pages.productionPhase.phaseWeighingAddModalObs')}</label>
                            <input
                                type="text"
                                placeholder="Motivo ou observacao..."
                                value={floatForm.obs}
                                onChange={e => setFloatForm(prev => ({ ...prev, obs: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={() => {
                                setShowFloatModal(false);
                                setFloatForm({ inciName: '', quantidade: '', obs: '' });
                            }}
                            style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.85rem' }}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={() => {
                                if (!floatForm.inciName.trim() || !floatForm.quantidade) {
                                    showToast(t('pages.productionPhase.phaseWeighingAddModalFillAll'), 'error');
                                    return;
                                }
                                setFloatMaterials(prev => [...prev, { ...floatForm }]);
                                setFloatForm({ inciName: '', quantidade: '', obs: '' });
                                setShowFloatModal(false);
                                showToast(t('pages.productionPhase.phaseWeighingAddModalAdded'), 'success');
                            }}
                            style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', backgroundColor: '#6366f1', color: 'white', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                        >
                            <FiPlus size={16} /> {t('pages.productionPhase.phaseWeighingAddModalAdd')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================================
    // RENDER: Documents Modal
    // ============================================================================
    const renderDocumentsModal = () => {
        if (!showDocumentsModal || !selectedOrderForDocs) return null;

        const statusInfo = getStatusBadge(selectedOrderForDocs.status);

        return (
            <div className={styles.modalBackdrop}>
                <div style={{
                    maxWidth: '550px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '24px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    width: '90%'
                }}>
                    <h2 style={{ margin: '0 0 8px 0' }}>
                        <FiFileText size={24} style={{ marginRight: '10px' }} />
                        {t('pages.productionPhase.documentsModalTitle', { opCode: selectedOrderForDocs.opCode })}
                    </h2>

                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6'
                    }}>
                        <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                            <strong><FiPackage size={14} style={{ marginRight: '6px' }} /> {t('pages.productionPhase.documentsProduct')}</strong>
                            {selectedOrderForDocs.items?.map((item, i) => (
                                <div key={i} style={{ marginTop: '4px' }}>
                                    • {item.productCode} - {item.productName}
                                    <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                                        ({t('pages.productionPhase.documentsBatch', { batch: item.lote || 'N/A' })})
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>
                            <strong>{t('pages.productionPhase.status')}:</strong>{' '}
                            <span style={statusInfo.style}>
                                {statusInfo.label}
                            </span>
                        </div>
                        {selectedOrderForDocs.completedAt && (
                            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#27ae60' }}>
                                <strong><FiCheck size={14} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.completedAt')}:</strong> {new Date(selectedOrderForDocs.completedAt).toLocaleString('pt-BR')}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* NOVO: Botão para Documento Único - Destacado */}
                        <button
                            onClick={() => handleDocumentExport('documento-unico')}
                            style={{
                                padding: '14px',
                                textAlign: 'left',
                                backgroundColor: '#1e1b4b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(30, 27, 75, 0.3)',
                            }}
                        >
                            <span>
                                <FiFileText size={18} style={{ marginRight: '10px' }} />
                                📄 Documento Único de Produção
                                <span style={{
                                    display: 'block',
                                    fontSize: '0.7rem',
                                    fontWeight: 'normal',
                                    opacity: 0.8,
                                    marginTop: '2px'
                                }}>
                                    (Compila todas as fases em um único PDF)
                                </span>
                            </span>
                            <FiDownload size={20} style={{ opacity: 0.9 }} />
                        </button>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            margin: '8px 0 4px 0',
                            color: '#6b7280',
                            fontSize: '0.75rem'
                        }}>
                            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e5e7eb' }} />
                            <span style={{ padding: '0 12px' }}>ou documentos individuais</span>
                            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e5e7eb' }} />
                        </div>

                        <button
                            onClick={() => handleDocumentExport('processo-manipulacao')}
                            style={{
                                padding: '12px',
                                textAlign: 'left',
                                backgroundColor: 'transparent',
                                color: '#8e44ad',
                                border: '1px solid #8e44ad',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                            }}
                        >
                            <span><FiFileText size={16} style={{ marginRight: '8px' }} /> Processo de Manipulação</span>
                            <FaChevronRight size={16} style={{ opacity: 0.9 }} />
                        </button>

                        <button
                            onClick={() => handleDocumentExport('ficha-pesagem')}
                            style={{
                                padding: '12px',
                                textAlign: 'left',
                                backgroundColor: 'transparent',
                                color: '#3498db',
                                border: '1px solid #3498db',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                            }}
                        >
                            <span><FiFileText size={16} style={{ marginRight: '8px' }} /> Ficha de Pesagem</span>
                            <FaChevronRight size={16} style={{ opacity: 0.9 }} />
                        </button>

                        <button
                            onClick={() => handleDocumentExport('processo-controle-qualidade')}
                            style={{
                                padding: '12px',
                                textAlign: 'left',
                                backgroundColor: 'transparent',
                                color: '#27ae60',
                                border: '1px solid #27ae60',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                            }}
                        >
                            <span><FiFileText size={16} style={{ marginRight: '8px' }} /> Controle de Qualidade</span>
                            <FaChevronRight size={16} style={{ opacity: 0.9 }} />
                        </button>
                    </div>

                    <div style={{ marginTop: '20px' }}>
                        <button
                            onClick={() => setShowDocumentsModal(false)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                backgroundColor: '#f1f3f5',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                color: '#495057'
                            }}
                        >
                            {t('pages.productionPhase.documentsClose')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================================
    // RENDER: Missing Materials Modal
    // ============================================================================
    const renderMissingMaterialsModal = () => {
        if (!showMissingMaterialsModal || !selectedMissingOrder) return null;

        const materialsArray = selectedMissingOrder.materialsRequired || [];
        const nonWaterMaterials = materialsArray.filter(m => !m.isWater && m.needed > 0);
        const stockMap = actualStockMap || {};

        const materialsWithStock = nonWaterMaterials.map(m => ({
            ...m,
            available: stockMap[m.id] || 0,
            missing: (stockMap[m.id] || 0) < m.needed
        }));

        const hasMissing = materialsWithStock.some(m => m.missing);

        return (
            <div className={styles.modalBackdrop}>
                <div style={{
                    maxWidth: '700px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '24px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    width: '90%',
                    maxHeight: '80vh',
                    overflowY: 'auto'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 style={{ margin: 0 }}>
                            <FiPackage size={24} style={{ marginRight: '10px', color: '#3498db' }} />
                            {t('pages.productionPhase.missingMaterials.title')} - {selectedMissingOrder.opCode}
                        </h2>
                        <button
                            onClick={() => setShowMissingMaterialsModal(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                color: '#666'
                            }}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    <div style={{
                        padding: '12px',
                        backgroundColor: hasMissing ? '#fff3cd' : '#d4edda',
                        border: hasMissing ? '1px solid #ffc107' : '1px solid #28a745',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        fontSize: '0.9rem'
                    }}>
                        {hasMissing ? (
                            <>
                                <FiAlertTriangle size={18} style={{ marginRight: '8px', color: '#856404' }} />
                                <strong>{t('pages.productionPhase.missingMaterials.insufficientStock')}</strong>
                                {' '}{t('pages.productionPhase.missingMaterials.checkMaterialsBelow')}
                            </>
                        ) : (
                            <>
                                <FiCheck size={18} style={{ marginRight: '8px', color: '#155724' }} />
                                <strong>{t('pages.productionPhase.missingMaterials.allMaterialsAvailable')}</strong>
                                {' '}{t('pages.productionPhase.missingMaterials.readyToStart')}
                            </>
                        )}
                    </div>

                    <div style={{
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6',
                        marginBottom: '16px'
                    }}>
                        <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                            <strong><FiInfo size={14} style={{ marginRight: '6px' }} /> {t('pages.productionPhase.missingMaterials.productInfo')}</strong>
                            {selectedMissingOrder.items?.map((item, i) => (
                                <div key={i} style={{ marginTop: '4px' }}>
                                    • {item.productCode} - {item.productName}
                                    <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                                        ({item.quantity} {item.mode === 'mass' ? 'Kg' : 'Kg'})
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#1e1b4b', color: 'white' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.78rem' }}>
                                        {t('pages.productionPhase.missingMaterials.material')}
                                    </th>
                                    <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.78rem' }}>
                                        {t('pages.productionPhase.missingMaterials.needed')}
                                    </th>
                                    <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.78rem' }}>
                                        {t('pages.productionPhase.missingMaterials.available')}
                                    </th>
                                    <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.78rem' }}>
                                        {t('pages.productionPhase.missingMaterials.status')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {materialsWithStock.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                                            {t('pages.productionPhase.missingMaterials.noMaterialsRequired')}
                                        </td>
                                    </tr>
                                ) : (
                                    materialsWithStock.map((mat, idx) => {
                                        const isMissing = mat.missing;
                                        return (
                                            <tr
                                                key={idx}
                                                style={{
                                                    backgroundColor: isMissing ? '#f8d7da' : (idx % 2 === 0 ? '#ffffff' : '#f9fafb'),
                                                    borderLeft: isMissing ? '3px solid #dc3545' : 'none'
                                                }}
                                            >
                                                <td style={{ padding: '8px 12px', fontSize: '0.85rem' }}>
                                                    {mat.name || mat.id}
                                                    {mat.isWater && (
                                                        <span style={{
                                                            marginLeft: '8px',
                                                            fontSize: '0.65rem',
                                                            padding: '2px 6px',
                                                            backgroundColor: '#e3f2fd',
                                                            borderRadius: '12px',
                                                            color: '#1976d2'
                                                        }}>
                                                            <FiDroplet size={12} style={{ marginRight: '4px' }} />
                                                            {t('pages.productionPhase.missingMaterials.water')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.85rem' }}>
                                                    {mat.needed.toFixed(3)} {mat.unit || 'Kg'}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.85rem' }}>
                                                    {mat.isWater ? '♾️' : mat.available.toFixed(3)} {mat.unit || 'Kg'}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.85rem' }}>
                                                    {mat.isWater ? (
                                                        <span style={{ color: '#28a745' }}>
                                                            <FiCheck size={14} style={{ marginRight: '4px' }} />
                                                            {t('pages.productionPhase.missingMaterials.ok')}
                                                        </span>
                                                    ) : isMissing ? (
                                                        <span style={{ color: '#dc3545' }}>
                                                            <FiAlertCircle size={14} style={{ marginRight: '4px' }} />
                                                            {t('pages.productionPhase.missingMaterials.missing')}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#28a745' }}>
                                                            <FiCheck size={14} style={{ marginRight: '4px' }} />
                                                            {t('pages.productionPhase.missingMaterials.ok')}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================================
    // MAIN RENDER
    // ============================================================================
    return (
        <div className={styles.pageContainer}>

            {/* Toast Notifications - Using reusable Toast component */}
            <div className={styles.toastContainer}>
                {toasts.map(n => (
                    <Toast
                        key={n.id}
                        message={n.message}
                        type={n.type}
                        duration={n.duration}
                        onClose={() => removeToast(n.id)}
                    />
                ))}
            </div>

            {/* Confirm Dialog - Using reusable ConfirmModal component */}
            {confirmDialog && (
                <ConfirmModal
                    isOpen={true}
                    onClose={closeConfirm}
                    onConfirm={() => {
                        confirmDialog.onConfirm();
                        closeConfirm();
                    }}
                    title={t('common.confirm')}
                    message={confirmDialog.message}
                    confirmText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    danger={true}
                />
            )}

            {/* Page Header - Using reusable PageHeader component */}
            <PageHeader
                title={t('pages.productionPhase.title')}
                showAction={false}
            />

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '24px', borderBottom: '2px solid #dee2e6' }}>
                {['pending', 'active', 'completed'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setSelectedTab(tab)}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderBottom: selectedTab === tab ? '3px solid #3498db' : '3px solid transparent',
                            color: selectedTab === tab ? '#3498db' : '#666',
                            fontWeight: selectedTab === tab ? 'bold' : 'normal',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            transition: 'all 0.2s'
                        }}
                    >
                        {tab === 'pending' && <><FiClock size={14} style={{ marginRight: '6px' }} /> {t('pages.productionPhase.tabPending', { count: pendingOrders.length })}</>}
                        {tab === 'active' && <><FiRefreshCw size={14} style={{ marginRight: '6px' }} /> {t('pages.productionPhase.tabActive', { count: activeOrders.length })}</>}
                        {tab === 'completed' && <><FiCheck size={14} style={{ marginRight: '6px' }} /> {t('pages.productionPhase.tabCompleted', { count: completedOrders.length })}</>}
                    </button>
                ))}
            </div>

            {/* Loading */}
            {isLoading && <div style={{ textAlign: 'center', padding: '40px' }}><FiRefreshCw size={24} className={styles.buttonSpinner} style={{ marginRight: '8px' }} /> {t('common.loading')}</div>}

            {/* PENDING ORDERS TAB */}
            {selectedTab === 'pending' && (
                <div>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('pages.productionPhase.tableOP')}</th>
                                    <th>{t('pages.productionPhase.tableDateTime')}</th>
                                    <th>{t('pages.productionPhase.tableProducts')}</th>
                                    <th>{t('pages.productionPhase.tableQty')}</th>
                                    <th>{t('pages.productionPhase.tableBatch')}</th>
                                    <th>{t('pages.productionPhase.tableStatus')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('pages.productionPhase.tableActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className={styles.emptyTable}>
                                            <EmptyState
                                                title={t('pages.productionPhase.noPending')}
                                                message={t('pages.productionPhase.noPendingMessage')}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    pendingOrders.map(order => {
                                        const statusInfo = order.status === 'awaiting-start'
                                            ? { label: <><FiPlay size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusAwaitingStart')}</>, color: '#3498db' }
                                            : { label: <><FiClock size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusAwaitingMP')}</>, color: '#f39c12' };

                                        const orderWithProduct = { ...order, id: order.id };
                                        const availablePhases = getProductAvailablePhases(orderWithProduct);

                                        return (
                                            <tr key={order.id}>
                                                <td><strong>{order.opCode}</strong></td>
                                                <td>{formatTimestamp(order.createdAt)}</td>
                                                <td>{order.items?.map((item, i) => (<div key={i}>{item.productCode} - {item.productName}</div>))}</td>
                                                <td>{order.items?.map((item, i) => (<div key={i}>{item.quantity} {item.mode === 'mass' ? 'Kg' : 'Un'}</div>))}</td>
                                                <td>{order.items?.map((item, i) => (<div key={i}>{item.lote || '—'}</div>))}</td>
                                                <td>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        backgroundColor: statusInfo.color,
                                                        color: 'white'
                                                    }}>
                                                        {statusInfo.label}
                                                    </span>
                                                    {availablePhases.length < ALL_SYSTEM_PHASES.length && (
                                                        <div style={{ fontSize: '0.65rem', marginTop: '4px', color: '#666' }}>
                                                            {t('pages.productionPhase.phasesConfigured', { count: availablePhases.length })}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className={styles.actionsContainer}>
                                                        {can.view && (
                                                            <button
                                                                className={`${styles.actionButton} ${styles.verifyButton}`}
                                                                onClick={() => handleViewMissingMaterials(order)}
                                                                title={t('pages.productionPhase.missingMaterials.view')}
                                                            >
                                                                <FiEye size={14} />
                                                            </button>
                                                        )}
                                                        {can.start && (
                                                            <button
                                                                className={`${styles.actionButton} ${styles.startButton}`}
                                                                onClick={() => handleActivateOrder(order.id)}
                                                                disabled={isActivatingOrder}
                                                            >
                                                                {isActivatingOrder ? <FiRefreshCw size={14} className={styles.buttonSpinner} /> : <FiPlay size={14} />}
                                                            </button>
                                                        )}
                                                        {can.cancel && (
                                                            <button
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                onClick={() => handleCancelOrder(order.id)}
                                                                disabled={isActivatingOrder}
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ACTIVE ORDERS TAB */}
            {selectedTab === 'active' && (
                <div>
                    <h2 className={styles.sectionTitle}><FiRefreshCw size={20} style={{ marginRight: '10px' }} /> {t('pages.productionPhase.activeOrdersTitle')}</h2>
                    {activeOrders.length === 0 ? (
                        <EmptyState
                            title={t('pages.productionPhase.noActive')}
                            message={t('pages.productionPhase.noActiveMessage')}
                        />
                    ) : (
                        activeOrders.map(order => (
                            <div key={order.id} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #dee2e6' }}>
                                    <div>
                                        <h3 style={{ margin: 0 }}>{t('pages.productionPhase.op')}: {order.opCode}</h3>
                                        <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px' }}>
                                            {order.items?.map((item, i) => (
                                                <span key={i}>
                                                    {item.productCode} - {item.productName} ({item.quantity} {item.mode === 'mass' ? 'Kg' : 'Un'}) | {t('pages.productionPhase.batch')}: {item.lote || 'N/A'}
                                                    {i < order.items.length - 1 ? ' | ' : ''}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '4px 12px',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            backgroundColor: '#27ae60',
                                            color: 'white'
                                        }}>
                                            <FiRefreshCw size={12} style={{ marginRight: '4px' }} /> {t('pages.productionPhase.statusActive')}
                                        </span>
                                    </div>
                                </div>
                                {renderPhaseTimeline(order)}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* COMPLETED ORDERS TAB */}
            {selectedTab === 'completed' && (
                <div>
                    <h2 className={styles.sectionTitle}><FiCheck size={20} style={{ marginRight: '10px' }} /> {t('pages.productionPhase.completedOrdersTitle')}</h2>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('pages.productionPhase.tableOP')}</th>
                                    <th>{t('pages.productionPhase.tableCreatedAt')}</th>
                                    <th>{t('pages.productionPhase.tableCompletedAt')}</th>
                                    <th>{t('pages.productionPhase.tableProducts')}</th>
                                    <th>{t('pages.productionPhase.tableQty')}</th>
                                    <th>{t('pages.productionPhase.tableBatch')}</th>
                                    <th style={{ width: '50px' }}>{t('pages.productionPhase.tableStatus')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('pages.productionPhase.tableDocuments')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {completedOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className={styles.emptyTable}>
                                            <EmptyState
                                                title={t('pages.productionPhase.noCompleted')}
                                                message={t('pages.productionPhase.noCompletedMessage')}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    completedOrders.map(order => {
                                        const statusInfo = getStatusBadge(order.status);
                                        return (
                                            <tr key={order.id}>
                                                <td><strong>{order.opCode}</strong></td>
                                                <td>{formatTimestamp(order.createdAt)}</td>
                                                <td>
                                                    {order.completedAt
                                                        ? new Date(order.completedAt).toLocaleString('pt-BR')
                                                        : order.phaseHistory?.slice(-1)[0]?.completedAt
                                                            ? new Date(order.phaseHistory.slice(-1)[0].completedAt).toLocaleString('pt-BR')
                                                            : '—'}
                                                </td>
                                                <td>{order.items?.map((item, i) => (<div key={i}>{item.productCode} - {item.productName}</div>))}</td>
                                                <td>{order.items?.map((item, i) => (<div key={i}>{item.quantity} {item.mode === 'mass' ? 'Kg' : 'Un'}</div>))}</td>
                                                <td>{order.items?.map((item, i) => (<div key={i}>{item.lote || '—'}</div>))}</td>
                                                <td>
                                                    <span style={statusInfo.style}>
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {can.generate && (
                                                        <button
                                                            className={`${styles.actionButton} ${styles.docsButton}`}
                                                            onClick={() => openDocumentsModal(order)}
                                                            title={t('pages.productionPhase.documentsEmit')}
                                                        >
                                                            <FiFileText size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Phase Detail Modal */}
            {renderPhaseDetailModal()}

            {/* Floating Material Modal */}
            {renderFloatMaterialModal()}

            {/* Documents Modal */}
            {renderDocumentsModal()}

            {/* Missing Materials Modal */}
            {renderMissingMaterialsModal()}
        </div>
    );
};

export default ProductionPhasePage;